import React, { Component } from "react";
import QRCode from "qrcode";

import shioriAvatar from "../images/shiori-avatar-cutout.png";

import {
  addMapCompanyMember,
  cancelMyMapOrder,
  createTwoFactorSetup,
  changeMyUserPassword,
  createDiscordLinkCode,
  createOpsuchtVerificationChallenge,
  createMapCompanyDefaultOrderTypes,
  createFeedbackEntry,
  createMapCompany,
  deleteMapCompany,
  disableTwoFactor,
  createMapOrder,
  downloadMapCompanyOrderSchematicZip,
  downloadMapCompanyOrderSourceImage,
  downloadMyMapOrderSchematicZip,
  downloadMyMapOrderSourceImage,
  findMatchingMapCompanies,
  enableTwoFactor,
  getAuthCaptcha,
  getCurrentOrderUser,
  getMapCompanyDiscordSettings,
  getMapCompanyLogoUrl,
  getMyMapCompanyLogoUrl,
  getOrderUserAvatarObjectUrl,
  getMyUserAvatarObjectUrl,
  getMyUserProfile,
  getOpsuchtVerification,
  getOrderSettings,
  getStoredOrderSession,
  listMapCompanyMembers,
  listMapCompanyJoinRequests,
  listMyMapCompanyJoinRequests,
  listMyMapCompanies,
  listMapCompanyOrderTypes,
  listMapCompanyOrders,
  listMyAchievements,
  listMyDirectMessages,
  listMyFeedbackEntries,
  listMyMapOrders,
  listOrderUsers,
  listPublicMapCompanies,
  loginOrderUser,
  logoutOrderUser,
  markMyDirectMessageRead,
  checkOpsuchtVerification,
  prepareNameChangeCheck,
  registerOrderUser,
  requestMapCompanyMembership,
  resolveMapCompanyJoinRequest,
  sendDirectMessageToUser,
  updateMapCompanyMember,
  updateMapCompanyDiscordSettings,
  updateMapCompanyOrderDiscount,
  updateMapCompanyOrderType,
  updateMapCompanyOrderStatus,
  updateMapCompanyOrderPayment,
  updateMapCompanyProfile,
  updateMyUserProfile,
  uploadMapCompanyLogo,
  unlockMyAchievement,
  uploadMyUserAvatar,
  uploadMyMapOrderSourceImage,
  uploadMyMapOrderSchematicZip,
} from "../services/orderBackendClient";

import "./orderAccountPanel.css";

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileScriptPromise = null;
const initialStoredOrderSession = getStoredOrderSession();
const THEME_KEYS = ["phoenixcorp", "sakura", "scifi", "sissy", "anime"];
const THEME_LABELS = {
  normal: "Standard",
  phoenixcorp: "PHCO",
  sakura: "Sakura",
  scifi: "SciFi",
  sissy: "Sissi",
  anime: "Anime",
};
const normalizeThemeKey = (themePreference) => {
  const cleanThemePreference = String(themePreference || "normal").trim().toLowerCase();
  return THEME_KEYS.includes(cleanThemePreference) ? cleanThemePreference : "normal";
};
const initialStoredTheme = normalizeThemeKey(initialStoredOrderSession?.user?.themePreference);

const ACHIEVEMENTS = [
  {
    id: "first_login",
    title: "Angekommen",
    description: "Zum ersten Mal im Auftragssystem angemeldet.",
  },
  {
    id: "first_order",
    title: "Erster Kartenauftrag",
    description: "Den ersten MapArt-Auftrag an eine Kartenfirma gesendet.",
  },
  {
    id: "first_join_request",
    title: "Angeklopft",
    description: "Zum ersten Mal Mitarbeit bei einer Kartenfirma angefragt.",
  },
  {
    id: "first_dm",
    title: "Erste Nachricht",
    description: "Eine Direktnachricht an einen anderen Nutzer gesendet.",
  },
  {
    id: "first_feedback",
    title: "Community-Stimme",
    description: "Zum ersten Mal Feedback fuer MapArtCraft eingereicht.",
  },
  {
    id: "profile_polished",
    title: "Profil poliert",
    description: "Das eigene Profil angepasst.",
  },
  {
    id: "profile_preview",
    title: "Spiegelbild",
    description: "Die eigene Profilvorschau geoeffnet.",
  },
  {
    id: "profile_image",
    title: "Gesicht gezeigt",
    description: "Ein Profilbild zur Pruefung hochgeladen.",
  },
  {
    id: "opsucht_verified",
    title: "OPSUCHT bestaetigt",
    description: "Den Ingame-Namen erfolgreich verifiziert.",
  },
  {
    id: "quiet_signal",
    title: "Leises Signal",
    description: "Etwas Verborgenes entdeckt.",
    secret: true,
  },
  {
    id: "ember_trail",
    title: "Funkenpfad",
    description: "Eine zweite versteckte Spur gefunden.",
    secret: true,
  },
  {
    id: "phoenix_colors",
    title: "Hausfarben",
    description: "Ein verborgenes Studio-Design als persoenlichen Standard festgelegt.",
    secret: true,
  },
  {
    id: "map_spark",
    title: "Leuchtspur",
    description: "Ein kleines Signal im Studio gefunden.",
    secret: true,
  },
  {
    id: "citybuild_cipher",
    title: "CityBuild-Code",
    description: "Eine versteckte Koordinate zwischen den CityBuilds erkannt.",
    secret: true,
  },
  {
    id: "sync_echo",
    title: "Sync-Echo",
    description: "Eine Spur gefunden, die nach Shiori klingt.",
    secret: true,
  },
  {
    id: "opsucht_final",
    title: "OPSUCHT.NET antwortet",
    description: "Ein sehr direktes Hallo aus dem Netzwerk erhalten.",
    secret: true,
  },
  {
    id: "flammy_found",
    title: "Flammy gefunden",
    description: "Eine kleine versteckte Flamme im Studio entdeckt.",
    secret: true,
  },
  {
    id: "phoenix_found",
    title: "Phoenix-Siegel",
    description: "Ein verstecktes PhoenixCorp-Signal aufgespuert.",
    secret: true,
  },
];

const ACHIEVEMENT_BY_ID = new Map(ACHIEVEMENTS.map((achievement) => [achievement.id, achievement]));

const formatBerlinDateTime = (value) => {
  if (!value) return "";
  const normalizedValue =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
      ? `${value.replace(" ", "T")}Z`
      : value;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Europe/Berlin",
  }).format(date);
};

const loadTurnstileScript = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_URL}"]`);
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.turnstile));
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error("Cloudflare Turnstile konnte nicht geladen werden."));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

class OrderAccountPanel extends Component {
  turnstileContainerRef = React.createRef();
  hiddenInputTrail = "";
  achievementToastTimer = null;
  opsNetFlashTimer = null;
  easterEggEffectTimer = null;
  shioriAudioContext = null;

  state = {
    expanded: false,
    activeOrderTab: "create",
    providerDashboardTab: "orders",
    mode: "login",
    playerName: "",
    password: "",
    currentPassword: "",
    newPassword: "",
    newPasswordConfirm: "",
    twoFactorCode: "",
    captcha: null,
    turnstileToken: "",
    turnstileWidgetId: null,
    twoFactorSetup: null,
    twoFactorQrCodeDataUrl: "",
    twoFactorSetupCode: "",
    twoFactorDisableCode: "",
    profileDisplayName: "",
    profileText: "",
    profileAvatarUrl: "",
    discordLinkCode: null,
    opsuchtVerification: null,
    companyName: "",
    companyDescription: "",
    user: initialStoredOrderSession?.session?.token ? initialStoredOrderSession.user : null,
    companies: [],
    publicCompanies: [],
    availableCityBuilds: [],
    selectedCompanySlug: "",
    selectedCompanyProfile: null,
    orderTypes: [],
    orderRequestVariant: "original",
    orderRequestBudget: "",
    orderRequestNotes: "",
    matchingCompanies: [],
    customerOrders: [],
    companyOrders: [],
    companyMembers: [],
    companyJoinRequests: [],
    companyDiscordSettings: null,
    companyDiscordGuilds: [],
    companyDiscordInviteUrl: "",
    companyDiscordRequiredPermissions: [],
    companyArchiveExpanded: false,
    companyLogoLoadFailures: {},
    directoryUsers: [],
    directorySearch: "",
    directorySelectedUserId: "",
    directoryAvatarUrls: {},
    directMessageText: "",
    directMessages: [],
    directMessageBox: "inbox",
    selectedDirectMessageId: "",
    unreadDirectMessageCount: 0,
    feedbackEntries: [],
    feedbackCategory: "bug",
    feedbackTitle: "",
    feedbackDescription: "",
    feedbackAffectedArea: "",
    feedbackReproductionSteps: "",
    feedbackExpectedBehavior: "",
    feedbackActualBehavior: "",
    feedbackImpact: "",
    feedbackAttachment: null,
    feedbackSubmitting: false,
    achievements: [],
    achievementToast: null,
    easterEggEffect: null,
    opsNetFlashActive: false,
    activeTheme: initialStoredTheme,
    lockedTheme: initialStoredTheme,
    auraActive: false,
    phoenixCorpThemeActive: initialStoredTheme === "phoenixcorp",
    phoenixCorpThemeLocked: initialStoredTheme === "phoenixcorp",
    myJoinRequests: [],
    memberPlayerName: "",
    memberRole: "member",
    joinRequestMessage: "",
    previewImageModal: null,
    profilePreviewOpen: false,
    latestCompany: null,
    loading: false,
    message: "",
    error: "",
  };

  componentDidMount() {
    this.applyThemeClasses();
    this.loadSettings();
    getCurrentOrderUser()
      .then((session) => {
        if (session?.user) {
          const themePreference = normalizeThemeKey(session.user.themePreference);
          this.setState({
            user: session.user,
            profileDisplayName: session.user.displayName || session.user.playerName,
            profileText: session.user.profileText || "",
            activeTheme: themePreference,
            lockedTheme: themePreference,
            phoenixCorpThemeActive: themePreference === "phoenixcorp",
            phoenixCorpThemeLocked: themePreference === "phoenixcorp",
          });
          this.loadMyProfile();
          this.loadOpsuchtVerification();
          this.loadCompanies();
          this.loadPublicCompanies();
          this.loadMyJoinRequests();
          this.loadDirectMessages();
          this.loadFeedbackEntries();
          this.loadAchievements();
        } else {
          this.setState({
            user: null,
            activeTheme: "normal",
            lockedTheme: "normal",
            phoenixCorpThemeActive: false,
            phoenixCorpThemeLocked: false,
          });
        }
      })
      .catch(() => {
        this.setState({
          user: null,
          activeTheme: "normal",
          lockedTheme: "normal",
          phoenixCorpThemeActive: false,
          phoenixCorpThemeLocked: false,
        });
      });
    window.addEventListener("keydown", this.handleHiddenKeyTrail);
    window.addEventListener("mousemove", this.handleThemePointer);
  }

  componentWillUnmount() {
    if (this.opsuchtVerificationTimer) {
      window.clearTimeout(this.opsuchtVerificationTimer);
      this.opsuchtVerificationTimer = null;
    }
    if (this.state.profileAvatarUrl) {
      URL.revokeObjectURL(this.state.profileAvatarUrl);
    }
    Object.values(this.state.directoryAvatarUrls).forEach((url) => URL.revokeObjectURL(url));
    if (this.achievementToastTimer) {
      window.clearTimeout(this.achievementToastTimer);
      this.achievementToastTimer = null;
    }
    if (this.opsNetFlashTimer) {
      window.clearTimeout(this.opsNetFlashTimer);
      this.opsNetFlashTimer = null;
    }
    if (this.easterEggEffectTimer) {
      window.clearTimeout(this.easterEggEffectTimer);
      this.easterEggEffectTimer = null;
    }
    if (this.shioriAudioContext) {
      this.shioriAudioContext.close().catch(() => null);
      this.shioriAudioContext = null;
    }
    window.removeEventListener("keydown", this.handleHiddenKeyTrail);
    window.removeEventListener("mousemove", this.handleThemePointer);
    document.body.classList.remove("phoenixCorpMode");
    document.body.classList.remove("phoenixCorpThemeLocked");
    document.body.classList.remove("themeAuraActive");
    THEME_KEYS.forEach((themeKey) => document.body.classList.remove(`${themeKey}ThemeMode`));
  }

  componentDidUpdate() {
    if (this.state.mode === "register") {
      this.renderTurnstileWidget();
    }
    this.applyThemeClasses();
  }

  applyThemeClasses = () => {
    const { activeTheme, auraActive, lockedTheme } = this.state;
    const normalizedActiveTheme = normalizeThemeKey(activeTheme);
    const normalizedLockedTheme = normalizeThemeKey(lockedTheme);
    THEME_KEYS.forEach((themeKey) => {
      document.body.classList.toggle(`${themeKey}ThemeMode`, normalizedActiveTheme === themeKey);
    });
    document.body.classList.toggle("phoenixCorpMode", normalizedActiveTheme === "phoenixcorp");
    document.body.classList.toggle("phoenixCorpThemeLocked", normalizedLockedTheme !== "normal");
    document.body.classList.toggle("themeAuraActive", auraActive && normalizedActiveTheme !== "normal");
    document.body.dataset.activeTheme = normalizedActiveTheme;
  };

  handleThemePointer = (event) => {
    if (!this.state.auraActive || normalizeThemeKey(this.state.activeTheme) === "normal") return;
    document.body.style.setProperty("--theme-pointer-x", `${event.clientX}px`);
    document.body.style.setProperty("--theme-pointer-y", `${event.clientY}px`);
  };

  updateField = (fieldName, value) => {
    this.setState({ [fieldName]: value, error: "", message: "" });
  };

  handleHiddenKeyTrail = (event) => {
    if (event.ctrlKey || event.altKey || event.metaKey || event.key.length !== 1) return;
    this.hiddenInputTrail = `${this.hiddenInputTrail}${event.key.toLowerCase()}`.slice(-32);
    if (this.hiddenInputTrail.endsWith("phco")) {
      this.activateHiddenTheme("phoenixcorp");
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("lock")) {
      this.toggleThemeLock();
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("aura")) {
      this.toggleThemeAura();
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("sakura")) {
      this.activateHiddenTheme("sakura");
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("scifi")) {
      this.activateHiddenTheme("scifi");
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("sissi") || this.hiddenInputTrail.endsWith("sissy")) {
      this.activateHiddenTheme("sissy");
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("anime")) {
      this.activateHiddenTheme("anime");
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("syncecho")) {
      this.triggerEasterEggEffect("sync");
      this.unlockAchievement("sync_echo", { source: "trail" });
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("shiori")) {
      this.triggerEasterEggEffect("shiori");
      this.unlockAchievement("quiet_signal", { source: "trail" });
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("flammengott")) {
      this.triggerEasterEggEffect("flame");
      this.unlockAchievement("ember_trail", { source: "trail" });
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("mapart")) {
      this.triggerEasterEggEffect("mapart");
      this.unlockAchievement("map_spark", { source: "trail" });
      this.hiddenInputTrail = "";
      return;
    }
    if (this.hiddenInputTrail.endsWith("cb100")) {
      this.triggerEasterEggEffect("cipher");
      this.unlockAchievement("citybuild_cipher", { source: "trail" });
      this.hiddenInputTrail = "";
      return;
    }
    if (
      this.hiddenInputTrail.endsWith("opsucht.net") ||
      this.hiddenInputTrail.endsWith("opsucht") ||
      this.hiddenInputTrail.endsWith("op")
    ) {
      this.triggerOpsNetFlash();
      this.unlockAchievement("opsucht_final", { source: "trail" });
      this.hiddenInputTrail = "";
      return;
    }
  };

  playShioriTone = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      const context = this.shioriAudioContext || new AudioContext();
      this.shioriAudioContext = context;
      if (context.state === "suspended") context.resume().catch(() => null);
      const now = context.currentTime;

      const portalGain = context.createGain();
      portalGain.gain.setValueAtTime(0.0001, now);
      portalGain.gain.exponentialRampToValueAtTime(0.12, now + 0.08);
      portalGain.gain.exponentialRampToValueAtTime(0.001, now + 0.95);
      portalGain.connect(context.destination);

      const portal = context.createOscillator();
      portal.type = "sawtooth";
      portal.frequency.setValueAtTime(72, now);
      portal.frequency.exponentialRampToValueAtTime(148, now + 0.72);
      portal.connect(portalGain);
      portal.start(now);
      portal.stop(now + 1.02);

      [0.48, 0.62, 0.76].forEach((offset, index) => {
        const stepOscillator = context.createOscillator();
        const stepGain = context.createGain();
        stepOscillator.type = "square";
        stepOscillator.frequency.setValueAtTime(86 + index * 10, now + offset);
        stepGain.gain.setValueAtTime(0.0001, now + offset);
        stepGain.gain.linearRampToValueAtTime(0.04, now + offset + 0.012);
        stepGain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.12);
        stepOscillator.connect(stepGain);
        stepGain.connect(context.destination);
        stepOscillator.start(now + offset);
        stepOscillator.stop(now + offset + 0.14);
      });

      const frequencies = [392, 523.25, 659.25, 783.99, 1046.5];
      frequencies.forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const startAt = now + 0.9 + index * 0.075;
        oscillator.type = index % 2 ? "triangle" : "sine";
        oscillator.frequency.setValueAtTime(frequency * 0.985, startAt);
        oscillator.frequency.exponentialRampToValueAtTime(frequency * 1.015, startAt + 0.18);
        gain.gain.setValueAtTime(0, startAt);
        gain.gain.linearRampToValueAtTime(index === frequencies.length - 1 ? 0.075 : 0.052, startAt + 0.018);
        gain.gain.exponentialRampToValueAtTime(0.001, startAt + 0.34);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start(startAt);
        oscillator.stop(startAt + 0.36);
      });

      window.setTimeout(() => this.speakShioriLine(), 1050);
    } catch (error) {
      // Browser duerfen Audio blocken; der visuelle Effekt bleibt dann aktiv.
    }
  };

  speakShioriLine = () => {
    try {
      if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance("Shiori ist online. Ich unterstuetze dich bei deiner MapArt.");
      utterance.lang = "de-DE";
      const voices = window.speechSynthesis.getVoices ? window.speechSynthesis.getVoices() : [];
      const germanVoice =
        voices.find((voice) => voice.lang?.toLowerCase().startsWith("de") && /female|frau|katja|anna|petra|hortense/i.test(voice.name)) ||
        voices.find((voice) => voice.lang?.toLowerCase().startsWith("de")) ||
        null;
      if (germanVoice) utterance.voice = germanVoice;
      utterance.rate = 0.9;
      utterance.pitch = 0.88;
      utterance.volume = 0.92;
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      // Sprachausgabe ist optional und darf den Easter-Egg-Effekt nicht blockieren.
    }
  };

  triggerEasterEggEffect = (effect) => {
    if (this.easterEggEffectTimer) window.clearTimeout(this.easterEggEffectTimer);
    if (effect === "shiori") this.playShioriTone();
    this.setState({ easterEggEffect: null }, () => {
      window.setTimeout(() => this.setState({ easterEggEffect: effect }), 20);
    });
    this.easterEggEffectTimer = window.setTimeout(() => {
      this.setState({ easterEggEffect: null });
      this.easterEggEffectTimer = null;
    }, effect === "shiori" ? 3600 : 2200);
  };

  clickHiddenFigure = (figure) => {
    if (figure === "flammy") {
      this.triggerEasterEggEffect("flame");
      this.unlockAchievement("flammy_found", { source: "hidden-figure" });
      return;
    }

    if (figure === "phoenix") {
      this.triggerEasterEggEffect("phoenix");
      this.unlockAchievement("phoenix_found", { source: "hidden-figure" });
    }
  };

  openProfilePreview = () => {
    this.setState({ profilePreviewOpen: true });
    this.unlockAchievement("profile_preview", { source: "profile-panel" });
  };

  triggerOpsNetFlash = () => {
    if (this.opsNetFlashTimer) window.clearTimeout(this.opsNetFlashTimer);
    this.setState({ opsNetFlashActive: false }, () => {
      window.setTimeout(() => this.setState({ opsNetFlashActive: true }), 20);
    });
    this.opsNetFlashTimer = window.setTimeout(() => {
      this.setState({ opsNetFlashActive: false });
      this.opsNetFlashTimer = null;
    }, 1500);
  };

  activateHiddenTheme = (themeKey) => {
    const normalizedTheme = normalizeThemeKey(themeKey);
    if (normalizedTheme === "normal") return;
    this.setState((state) => {
      const nextTheme = normalizeThemeKey(state.activeTheme) === normalizedTheme ? "normal" : normalizedTheme;
      return {
        activeTheme: nextTheme,
        phoenixCorpThemeActive: nextTheme === "phoenixcorp",
        message:
          nextTheme === "normal"
            ? "Theme wieder auf Standard gestellt."
            : "Geheimes Theme aktiviert. Mit lock kannst du es als Standard speichern.",
        error: "",
      };
    });
  };

  toggleThemeAura = () => {
    if (normalizeThemeKey(this.state.activeTheme) === "normal") {
      this.setState({ error: "Aura braucht zuerst ein aktives Theme.", message: "" });
      return;
    }
    this.setState((state) => ({
      auraActive: !state.auraActive,
      message: state.auraActive ? "Theme-Aura deaktiviert." : "Theme-Aura aktiviert.",
      error: "",
    }));
  };

  toggleThemeLock = async () => {
    const { activeTheme, lockedTheme, profileText, user } = this.state;
    const normalizedActiveTheme = normalizeThemeKey(activeTheme);
    const normalizedLockedTheme = normalizeThemeKey(lockedTheme);
    if (!user) {
      this.setState({ error: "Theme-Lock geht nur, wenn du angemeldet bist.", message: "" });
      return;
    }
    if (normalizedActiveTheme === "normal" && normalizedLockedTheme === "normal") {
      this.setState({ error: "Aktiviere zuerst ein geheimes Theme, dann kannst du es mit lock speichern.", message: "" });
      return;
    }

    const shouldUnlockCurrentTheme = normalizedActiveTheme !== "normal" && normalizedActiveTheme === normalizedLockedTheme;
    const nextThemePreference = shouldUnlockCurrentTheme
      ? "normal"
      : normalizedActiveTheme !== "normal"
        ? normalizedActiveTheme
        : "normal";
    try {
      const data = await updateMyUserProfile({
        profileText,
        themePreference: nextThemePreference,
      });
      const savedTheme = normalizeThemeKey(data.user.themePreference);
      const nextActiveTheme = savedTheme === "normal" ? normalizedActiveTheme : savedTheme;
      const lockedThemeLabel = THEME_LABELS[savedTheme] || "Theme";
      this.setState({
        user: data.user,
        activeTheme: nextActiveTheme,
        lockedTheme: savedTheme,
        phoenixCorpThemeActive: nextActiveTheme === "phoenixcorp",
        phoenixCorpThemeLocked: savedTheme === "phoenixcorp",
        message:
          savedTheme === "normal"
            ? "Theme ist nicht mehr als Standard gespeichert."
            : `${lockedThemeLabel} ist jetzt dein Standard-Design.`,
        error: "",
      });
      if (savedTheme !== "normal") this.unlockAchievement("phoenix_colors", { source: "theme-lock", theme: savedTheme });
    } catch (error) {
      this.setState({ error: error.message, message: "" });
    }
  };

  loadAchievements = async () => {
    try {
      const data = await listMyAchievements();
      this.setState({ achievements: data.achievements || [] });
    } catch (error) {
      this.setState({ achievements: [] });
    }
  };

  unlockAchievement = async (achievementId, metadata = {}) => {
    if (!this.state.user) return;

    try {
      const data = await unlockMyAchievement({ achievementId, metadata });
      if (!data.achievement) return;
      const alreadyUnlocked = this.state.achievements.some((achievement) => achievement.id === data.achievement.id);
      this.setState((state) => ({
        achievements: state.achievements.some((achievement) => achievement.id === data.achievement.id)
          ? state.achievements
          : [data.achievement, ...state.achievements],
        achievementToast: data.achievement.id,
      }));
      if (data.newlyUnlocked || alreadyUnlocked) {
        if (this.achievementToastTimer) window.clearTimeout(this.achievementToastTimer);
        this.achievementToastTimer = window.setTimeout(() => {
          this.setState({ achievementToast: null });
        }, 5200);
      }
    } catch (error) {
      // Achievements sollen die eigentliche Nutzeraktion nie blockieren.
    }
  };

  setMode = (mode) => {
    this.resetTurnstileWidget();
    this.setState({ mode, error: "", message: "", turnstileToken: "", twoFactorCode: "" }, () => {
      if (mode === "register") this.loadCaptcha();
    });
  };

  loadCaptcha = async () => {
    try {
      const data = await getAuthCaptcha();
      this.resetTurnstileWidget();
      this.setState({ captcha: data.captcha || null, turnstileToken: "" });
    } catch (error) {
      this.setState({ captcha: null });
    }
  };

  resetTurnstileWidget = () => {
    if (window.turnstile && this.state.turnstileWidgetId !== null) {
      window.turnstile.reset(this.state.turnstileWidgetId);
    }
  };

  renderTurnstileWidget = async () => {
    const { captcha, mode, turnstileWidgetId } = this.state;
    if (mode !== "register" || captcha?.provider !== "turnstile" || !captcha.siteKey || turnstileWidgetId !== null) return;
    if (!this.turnstileContainerRef.current) return;

    try {
      const turnstile = await loadTurnstileScript();
      if (!turnstile || this.state.turnstileWidgetId !== null || !this.turnstileContainerRef.current) return;

      const widgetId = turnstile.render(this.turnstileContainerRef.current, {
        sitekey: captcha.siteKey,
        theme: "dark",
        size: "flexible",
        callback: (token) => this.setState({ turnstileToken: token, error: "" }),
        "expired-callback": () => this.setState({ turnstileToken: "" }),
        "error-callback": () =>
          this.setState({
            turnstileToken: "",
            error: "Cloudflare-Sicherheitspruefung konnte nicht geladen werden.",
          }),
      });
      this.setState({ turnstileWidgetId: widgetId });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  setActiveOrderTab = (activeOrderTab) => {
    this.setState({ activeOrderTab, error: "", message: "" });
    if (activeOrderTab === "orders") {
      this.loadCustomerOrders();
    }
    if (activeOrderTab === "users") {
      this.loadDirectoryUsers();
    }
    if (activeOrderTab === "messages") {
      this.loadDirectMessages();
    }
    if (activeOrderTab === "feedback") {
      this.loadFeedbackEntries();
    }
  };

  setProviderDashboardTab = (providerDashboardTab) => {
    this.setState({ providerDashboardTab, error: "", message: "" });
    if (providerDashboardTab === "orders") {
      this.loadCompanyOrders();
    }
    if (providerDashboardTab === "members") {
      this.loadCompanyMembers();
      this.loadCompanyJoinRequests();
    }
    if (providerDashboardTab === "discord") {
      this.loadCompanyDiscordSettings();
    }
  };

  loadSettings = async () => {
    try {
      const settings = await getOrderSettings();
      this.setState({ availableCityBuilds: settings.availableCityBuilds || [] });
    } catch (error) {
      this.setState({ availableCityBuilds: [] });
    }
  };

  toggleExpanded = () => {
    this.setState((state) => ({ expanded: !state.expanded }));
  };

  submitAuth = async (event) => {
    event.preventDefault();
    const { mode, password, playerName, turnstileToken, twoFactorCode } = this.state;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const session =
        mode === "register"
          ? await registerOrderUser({ playerName, password, turnstileToken })
          : await loginOrderUser({ playerName, password, twoFactorCode });
      const themePreference = normalizeThemeKey(session.user.themePreference);

      this.setState({
        expanded: true,
        user: session.user,
        password: "",
        twoFactorCode: "",
        turnstileToken: "",
        activeTheme: themePreference,
        lockedTheme: themePreference,
        phoenixCorpThemeActive: themePreference === "phoenixcorp",
        phoenixCorpThemeLocked: themePreference === "phoenixcorp",
        message: mode === "register" ? "Account erstellt und angemeldet." : "Erfolgreich angemeldet.",
      });
      this.loadCompanies();
      this.loadCustomerOrders();
      this.loadPublicCompanies();
      this.loadMyJoinRequests();
      this.loadOpsuchtVerification();
      this.loadDirectMessages();
      this.loadAchievements();
      this.unlockAchievement("first_login", { mode });
    } catch (error) {
      this.setState({ error: error.message });
      if (mode === "register") this.loadCaptcha();
    } finally {
      this.setState({ loading: false });
    }
  };

  loadMyProfile = async () => {
    try {
      const data = await getMyUserProfile();
      const themePreference = normalizeThemeKey(data.user.themePreference);
      this.setState({
        user: data.user,
        profileDisplayName: data.user.displayName || data.user.playerName,
        profileText: data.user.profileText || "",
        activeTheme: themePreference,
        lockedTheme: themePreference,
        phoenixCorpThemeActive: themePreference === "phoenixcorp",
        phoenixCorpThemeLocked: themePreference === "phoenixcorp",
      });
      this.loadMyAvatarPreview(data.user);
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  loadDirectoryUsers = async () => {
    const { directorySearch, directoryAvatarUrls, directorySelectedUserId } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      Object.values(directoryAvatarUrls).forEach((url) => URL.revokeObjectURL(url));
      const data = await listOrderUsers({ search: directorySearch });
      const users = data.users || [];
      const selectedUserStillVisible = users.some((entry) => String(entry.id) === String(directorySelectedUserId));
      this.setState({
        directoryUsers: users,
        directorySelectedUserId: selectedUserStillVisible ? directorySelectedUserId : users[0]?.id || "",
        directoryAvatarUrls: {},
      });

      const avatarEntries = await Promise.all(
        users
          .filter((entry) => entry.hasApprovedAvatar)
          .map(async (entry) => {
            const url = await getOrderUserAvatarObjectUrl({ userId: entry.id }).catch(() => "");
            return [entry.id, url];
          })
      );
      this.setState({
        directoryAvatarUrls: Object.fromEntries(avatarEntries.filter(([, url]) => Boolean(url))),
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  selectDirectoryUser = (directorySelectedUserId) => {
    this.setState({ directorySelectedUserId, directMessageText: "", error: "", message: "" });
  };

  sendDirectoryDirectMessage = async () => {
    const { directMessageText, directorySelectedUserId } = this.state;
    if (!directorySelectedUserId) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await sendDirectMessageToUser({
        userId: directorySelectedUserId,
        body: directMessageText,
      });
      this.setState({
        directMessageText: "",
        message: `DM an ${data.directMessage?.recipientPlayerName || "Nutzer"} gesendet.`,
      });
      this.unlockAchievement("first_dm", { recipientUserId: directorySelectedUserId });
      this.loadDirectMessages("sent");
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  loadDirectMessages = async (box = this.state.directMessageBox) => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await listMyDirectMessages({ box });
      const messages = data.messages || [];
      this.setState((state) => {
        const selectedStillVisible = messages.some((entry) => String(entry.id) === String(state.selectedDirectMessageId));
        return {
          directMessageBox: box,
          directMessages: messages,
          selectedDirectMessageId: selectedStillVisible ? state.selectedDirectMessageId : messages[0]?.id || "",
          unreadDirectMessageCount: data.unreadCount || 0,
        };
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  selectDirectMessage = async (selectedDirectMessageId) => {
    const selectedMessage = this.state.directMessages.find((entry) => String(entry.id) === String(selectedDirectMessageId));
    this.setState({ selectedDirectMessageId, error: "", message: "" });

    if (selectedMessage?.direction === "received" && !selectedMessage.readAt) {
      try {
        await markMyDirectMessageRead({ messageId: selectedDirectMessageId });
        await this.loadDirectMessages("inbox");
      } catch (error) {
        this.setState({ error: error.message });
      }
    }
  };

  loadFeedbackEntries = async () => {
    if (!this.state.user) return;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await listMyFeedbackEntries();
      this.setState({ feedbackEntries: data.feedback || [] });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  updateFeedbackAttachment = (event) => {
    const file = event.target.files?.[0] || null;
    this.setState({ feedbackAttachment: file, error: "", message: "" });
  };

  submitFeedback = async (event) => {
    event.preventDefault();
    const {
      feedbackAffectedArea,
      feedbackAttachment,
      feedbackCategory,
      feedbackDescription,
      feedbackExpectedBehavior,
      feedbackImpact,
      feedbackReproductionSteps,
      feedbackTitle,
      feedbackActualBehavior,
      opsuchtVerification,
      user,
    } = this.state;

    if (!user) {
      this.setState({ error: "Bitte zuerst anmelden, um Feedback einzureichen.", message: "" });
      return;
    }

    if (!opsuchtVerification?.verified && !user.opsuchtVerified) {
      this.setState({
        error: "Um Feedback einzureichen, musst du deinen OPSUCHT-Spielaccount zunaechst verifizieren.",
        message: "",
      });
      return;
    }

    this.setState({ feedbackSubmitting: true, loading: true, error: "", message: "" });
    try {
      const browserInfo = `${navigator.userAgent || "Unbekannter Browser"} | ${window.innerWidth}x${window.innerHeight}`;
      const data = await createFeedbackEntry({
        feedback: {
          category: feedbackCategory,
          title: feedbackTitle,
          description: feedbackDescription,
          affectedArea: feedbackAffectedArea,
          reproductionSteps: feedbackReproductionSteps,
          expectedBehavior: feedbackExpectedBehavior,
          actualBehavior: feedbackActualBehavior,
          impact: feedbackImpact,
          browserInfo,
          route: window.location.pathname,
        },
        attachment: feedbackAttachment,
      });
      this.setState({
        feedbackEntries: [data.feedback, ...this.state.feedbackEntries],
        feedbackTitle: "",
        feedbackDescription: "",
        feedbackAffectedArea: "",
        feedbackReproductionSteps: "",
        feedbackExpectedBehavior: "",
        feedbackActualBehavior: "",
        feedbackImpact: "",
        feedbackAttachment: null,
        message: `Feedback ${data.feedback.publicId} wurde eingereicht.`,
      });
      this.unlockAchievement("first_feedback", { publicId: data.feedback.publicId });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ feedbackSubmitting: false, loading: false });
    }
  };

  loadOpsuchtVerification = async () => {
    try {
      const data = await getOpsuchtVerification();
      this.setState({ opsuchtVerification: data.opsuchtVerification || null });
      this.scheduleOpsuchtVerificationPoll(data.opsuchtVerification);
    } catch (error) {
      this.setState({ opsuchtVerification: null });
    }
  };

  scheduleOpsuchtVerificationPoll = (opsuchtVerification) => {
    if (this.opsuchtVerificationTimer) {
      window.clearTimeout(this.opsuchtVerificationTimer);
      this.opsuchtVerificationTimer = null;
    }

    if (!opsuchtVerification?.challenge || opsuchtVerification.verified) return;

    this.opsuchtVerificationTimer = window.setTimeout(() => {
      this.checkOpsuchtVerification({ silent: true });
    }, 30000);
  };

  startOpsuchtVerification = async () => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await createOpsuchtVerificationChallenge();
      this.setState({
        opsuchtVerification: data.opsuchtVerification,
        message: "OPSUCHT-Verifizierung gestartet.",
      });
      this.scheduleOpsuchtVerificationPoll(data.opsuchtVerification);
      this.loadMyProfile();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  checkOpsuchtVerification = async ({ silent = false } = {}) => {
    if (!silent) this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await checkOpsuchtVerification();
      this.setState({
        opsuchtVerification: data.opsuchtVerification,
        message: silent
          ? this.state.message
          : data.opsuchtVerification?.verified
          ? "OPSUCHT-Verifizierung erfolgreich."
          : "OPSUCHT wurde geprueft. Noch keine passende Auktion gefunden.",
      });
      if (data.opsuchtVerification?.verified) {
        this.unlockAchievement("opsucht_verified");
      }
      this.scheduleOpsuchtVerificationPoll(data.opsuchtVerification);
      this.loadMyProfile();
    } catch (error) {
      if (!silent) this.setState({ error: error.message });
      this.scheduleOpsuchtVerificationPoll(this.state.opsuchtVerification);
    } finally {
      if (!silent) this.setState({ loading: false });
    }
  };

  prepareNameChangeCheck = async () => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await prepareNameChangeCheck();
      this.setState({ message: data.message || "Namensaenderungspruefung vorbereitet." });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  loadMyAvatarPreview = async (user = this.state.user) => {
    if (!user || user.avatarReviewStatus === "none") {
      if (this.state.profileAvatarUrl) URL.revokeObjectURL(this.state.profileAvatarUrl);
      this.setState({ profileAvatarUrl: "" });
      return;
    }

    try {
      const profileAvatarUrl = await getMyUserAvatarObjectUrl();
      if (this.state.profileAvatarUrl) URL.revokeObjectURL(this.state.profileAvatarUrl);
      this.setState({ profileAvatarUrl });
    } catch (error) {
      if (this.state.profileAvatarUrl) URL.revokeObjectURL(this.state.profileAvatarUrl);
      this.setState({ profileAvatarUrl: "" });
    }
  };

  startTwoFactorSetup = async () => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await createTwoFactorSetup();
      const twoFactorQrCodeDataUrl = await QRCode.toDataURL(data.setup.otpAuthUri, {
        errorCorrectionLevel: "M",
        margin: 2,
        scale: 6,
        color: {
          dark: "#07111f",
          light: "#f4fff9",
        },
      });
      this.setState({
        twoFactorSetup: data.setup,
        twoFactorQrCodeDataUrl,
        twoFactorSetupCode: "",
        message: "2FA-Setup erstellt.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  enableTwoFactorForAccount = async () => {
    const { twoFactorSetupCode } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await enableTwoFactor({ code: twoFactorSetupCode });
      this.setState({
        user: data.user,
        twoFactorSetup: null,
        twoFactorQrCodeDataUrl: "",
        twoFactorSetupCode: "",
        message: "2FA ist aktiviert.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  disableTwoFactorForAccount = async () => {
    const { twoFactorDisableCode } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await disableTwoFactor({ code: twoFactorDisableCode });
      this.setState({
        user: data.user,
        twoFactorDisableCode: "",
        message: "2FA ist deaktiviert.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  saveMyProfile = async () => {
    const { profileText } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMyUserProfile({ profileText });
      const themePreference = normalizeThemeKey(data.user.themePreference);
      this.setState({
        user: data.user,
        profileDisplayName: data.user.displayName || data.user.playerName,
        profileText: data.user.profileText || "",
        lockedTheme: themePreference,
        phoenixCorpThemeLocked: themePreference === "phoenixcorp",
        message: "Profiltext gespeichert.",
      });
      this.unlockAchievement("profile_polished");
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  saveMyPassword = async () => {
    const { currentPassword, newPassword, newPasswordConfirm } = this.state;
    if (newPassword !== newPasswordConfirm) {
      this.setState({ error: "Die neuen Passwoerter stimmen nicht ueberein.", message: "" });
      return;
    }

    this.setState({ loading: true, error: "", message: "" });
    try {
      await changeMyUserPassword({ currentPassword, newPassword });
      this.setState({
        currentPassword: "",
        newPassword: "",
        newPasswordConfirm: "",
        message: "Passwort wurde geaendert.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  resizeImageFile = ({ file, maxSize = 512, outputName = "bild", errorLabel = "Bild", canvasSize = null, paddingRatio = 0 }) =>
    new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) {
        reject(new Error("Bitte ein Bild hochladen."));
        return;
      }

      const image = new Image();
      const objectUrl = URL.createObjectURL(file);
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const outputSize = canvasSize || null;
        const scale = outputSize
          ? Math.min((outputSize * (1 - paddingRatio * 2)) / image.width, (outputSize * (1 - paddingRatio * 2)) / image.height)
          : Math.min(1, maxSize / Math.max(image.width, image.height));
        const width = outputSize || Math.max(1, Math.round(image.width * scale));
        const height = outputSize || Math.max(1, Math.round(image.height * scale));
        const drawWidth = Math.max(1, Math.round(image.width * scale));
        const drawHeight = Math.max(1, Math.round(image.height * scale));
        const drawX = outputSize ? Math.round((outputSize - drawWidth) / 2) : 0;
        const drawY = outputSize ? Math.round((outputSize - drawHeight) / 2) : 0;

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        context.clearRect(0, 0, width, height);
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = "high";
        context.drawImage(image, drawX, drawY, drawWidth, drawHeight);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error(`${errorLabel} konnte nicht verarbeitet werden.`));
              return;
            }
            const outputType = blob.type || "image/png";
            const extension = outputType === "image/webp" ? "webp" : outputType === "image/jpeg" ? "jpg" : "png";
            const resizedFile = new File([blob], `${outputName}.${extension}`, { type: outputType });
            resolve(resizedFile);
          },
          "image/webp",
          0.86
        );
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`${errorLabel} konnte nicht gelesen werden.`));
      };
      image.src = objectUrl;
    });

  resizeAvatarFile = (file) =>
    this.resizeImageFile({
      file,
      maxSize: 512,
      outputName: "profilbild",
      errorLabel: "Profilbild",
    });

  resizeCompanyLogoFile = (file) =>
    this.resizeImageFile({
      file,
      maxSize: 512,
      canvasSize: 512,
      paddingRatio: 0.08,
      outputName: "firmenlogo",
      errorLabel: "Firmenlogo",
    });

  uploadMyAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const resizedFile = await this.resizeAvatarFile(file);
      const data = await uploadMyUserAvatar({ file: resizedFile });
      this.setState({
        user: data.user,
        message:
          "Profilbild hochgeladen und verkleinert. Es bleibt ohne Ablauf in Pruefung und wird erst nach Admin-Genehmigung sichtbar.",
      });
      this.unlockAchievement("profile_image");
      this.loadMyAvatarPreview(data.user);
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      event.target.value = "";
      this.setState({ loading: false });
    }
  };

  uploadSelectedCompanyLogo = async (event) => {
    const file = event.target.files?.[0];
    const { selectedCompanySlug } = this.state;
    if (!file || !selectedCompanySlug) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const resizedFile = await this.resizeCompanyLogoFile(file);
      const data = await uploadMapCompanyLogo({ slug: selectedCompanySlug, file: resizedFile });
      this.setState({
        selectedCompanyProfile: { ...data.organization },
        companies: this.state.companies.map((company) =>
          company.slug === data.organization.slug ? { ...company, ...data.organization } : company
        ),
        message: "Firmenlogo automatisch zentriert, auf 512x512 angepasst und hochgeladen. Es wird erst nach Admin-Genehmigung oeffentlich angezeigt.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      event.target.value = "";
      this.setState({ loading: false });
    }
  };

  prepareDiscordLink = async () => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await createDiscordLinkCode();
      this.setState({
        discordLinkCode: data.discordLink,
        message: "Discord-Kopplung vorbereitet.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  submitCompany = async (event) => {
    event.preventDefault();
    const { companyDescription, companyName } = this.state;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await createMapCompany({
        name: companyName,
        description: companyDescription,
      });

      this.setState({
        companyName: "",
        companyDescription: "",
        latestCompany: data.organization,
        companies: [data.organization, ...this.state.companies],
        selectedCompanySlug: data.organization.slug,
        selectedCompanyProfile: { ...data.organization },
        message: "Kartenfirma wurde angelegt.",
      });
      this.loadOrderTypes(data.organization.slug);
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  logout = async () => {
    this.setState({ loading: true, error: "", message: "" });
    await logoutOrderUser();
    this.setState({
      user: null,
      companies: [],
      publicCompanies: [],
      selectedCompanySlug: "",
      selectedCompanyProfile: null,
      orderTypes: [],
      companyJoinRequests: [],
      myJoinRequests: [],
      latestCompany: null,
      activeTheme: "normal",
      lockedTheme: "normal",
      auraActive: false,
      phoenixCorpThemeActive: false,
      phoenixCorpThemeLocked: false,
      loading: false,
      message: "Abgemeldet.",
    });
  };

  loadCompanies = async () => {
    try {
      const data = await listMyMapCompanies();
      const companies = data.organizations || [];
      this.setState({
        companies,
        selectedCompanySlug: this.state.selectedCompanySlug || companies[0]?.slug || "",
        selectedCompanyProfile: companies[0] ? { ...companies[0] } : null,
      });
      if (companies[0] && !this.state.selectedCompanySlug) {
        this.loadOrderTypes(companies[0].slug);
        this.loadCompanyOrders(companies[0].slug);
        this.loadCompanyMembers(companies[0].slug);
      }
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  loadPublicCompanies = async () => {
    try {
      const data = await listPublicMapCompanies();
      this.setState({ publicCompanies: data.organizations || [], companyLogoLoadFailures: {} });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  loadMyJoinRequests = async () => {
    try {
      const data = await listMyMapCompanyJoinRequests();
      this.setState({ myJoinRequests: data.joinRequests || [] });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  selectCompany = (slug) => {
    const company = this.state.companies.find((item) => item.slug === slug);
    this.setState({
      selectedCompanySlug: slug,
      selectedCompanyProfile: company ? { ...company } : null,
      orderTypes: [],
      companyOrders: [],
      companyMembers: [],
      companyDiscordSettings: null,
      companyDiscordGuilds: [],
      error: "",
      message: "",
    });
    if (slug) {
      this.loadOrderTypes(slug);
      this.loadCompanyOrders(slug);
      this.loadCompanyMembers(slug);
      this.loadCompanyJoinRequests(slug);
      if (this.state.providerDashboardTab === "discord") {
        this.loadCompanyDiscordSettings(slug);
      }
    }
  };

  updateProfileField = (fieldName, value) => {
    this.setState((state) => ({
      selectedCompanyProfile: {
        ...state.selectedCompanyProfile,
        [fieldName]: value,
      },
      error: "",
      message: "",
    }));
  };

  updateDiscordSettingField = (fieldName, value) => {
    this.setState((state) => ({
      companyDiscordSettings: {
        ...(state.companyDiscordSettings || {}),
        [fieldName]: value,
      },
      error: "",
      message: "",
    }));
  };

  loadCompanyDiscordSettings = async (slug = this.state.selectedCompanySlug) => {
    if (!slug) return;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await getMapCompanyDiscordSettings({ slug });
      this.setState({
        companyDiscordSettings: data.settings || null,
        companyDiscordGuilds: data.guilds || [],
        companyDiscordInviteUrl: data.inviteUrl || "",
        companyDiscordRequiredPermissions: data.requiredPermissions || [],
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  saveCompanyDiscordSettings = async (event) => {
    event.preventDefault();
    const { companyDiscordSettings, selectedCompanySlug } = this.state;
    if (!selectedCompanySlug || !companyDiscordSettings) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMapCompanyDiscordSettings({
        slug: selectedCompanySlug,
        settings: companyDiscordSettings,
      });
      this.setState({
        companyDiscordSettings: data.settings || null,
        companyDiscordGuilds: data.guilds || [],
        companyDiscordInviteUrl: data.inviteUrl || "",
        companyDiscordRequiredPermissions: data.requiredPermissions || [],
        message: "Discord-Ziel gespeichert.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  saveSelectedCompanyProfile = async (event) => {
    event.preventDefault();
    const { selectedCompanyProfile, selectedCompanySlug } = this.state;
    if (!selectedCompanySlug || !selectedCompanyProfile) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMapCompanyProfile({
        slug: selectedCompanySlug,
        profile: selectedCompanyProfile,
      });
      const companies = this.state.companies.map((company) =>
        company.slug === data.organization.slug ? { ...company, ...data.organization } : company
      );
      this.setState({
        companies,
        selectedCompanyProfile: { ...data.organization },
        message: "Kartenfirma-Profil gespeichert.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  deleteSelectedCompany = async () => {
    const { selectedCompanyProfile, selectedCompanySlug } = this.state;
    if (!selectedCompanySlug || !selectedCompanyProfile) return;

    const confirmation = window.prompt(
      `Wenn du ${selectedCompanyProfile.name} wirklich loeschen willst, schreibe den Firmennamen exakt hier rein.`
    );
    if (confirmation !== selectedCompanyProfile.name) {
      this.setState({ error: "Loeschen abgebrochen. Der Firmenname wurde nicht exakt bestaetigt.", message: "" });
      return;
    }

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await deleteMapCompany({ slug: selectedCompanySlug });
      const nextCompanies = this.state.companies.filter((company) => company.slug !== selectedCompanySlug);
      const nextSelectedCompany = nextCompanies[0] || null;
      this.setState({
        companies: nextCompanies,
        selectedCompanySlug: nextSelectedCompany?.slug || "",
        selectedCompanyProfile: nextSelectedCompany ? { ...nextSelectedCompany } : null,
        orderTypes: [],
        companyOrders: [],
        companyMembers: [],
        companyJoinRequests: [],
        companyDiscordSettings: null,
        message: `${data.name || selectedCompanyProfile.name} wurde geloescht.`,
      });
      this.loadPublicCompanies();
      if (nextSelectedCompany) {
        this.loadOrderTypes(nextSelectedCompany.slug);
        this.loadCompanyOrders(nextSelectedCompany.slug);
        this.loadCompanyMembers(nextSelectedCompany.slug);
      }
    } catch (error) {
      this.setState({ error: error.message, message: "" });
    } finally {
      this.setState({ loading: false });
    }
  };

  loadOrderTypes = async (slug = this.state.selectedCompanySlug) => {
    if (!slug) return;
    try {
      const data = await listMapCompanyOrderTypes({ slug });
      this.setState({ orderTypes: data.orderTypes || [] });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  createDefaultTypes = async () => {
    const { selectedCompanySlug } = this.state;
    if (!selectedCompanySlug) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await createMapCompanyDefaultOrderTypes({ slug: selectedCompanySlug });
      this.setState({
        orderTypes: data.orderTypes || [],
        selectedCompanyProfile: data.organization || this.state.selectedCompanyProfile,
        message: `${data.createdCount || 0} Standard-Auftragstyp(en) angelegt.`,
      });
      this.loadCompanies();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  updateOrderTypeField = (orderTypeId, fieldName, value) => {
    this.setState((state) => ({
      orderTypes: state.orderTypes.map((orderType) =>
        orderType.id === orderTypeId ? { ...orderType, [fieldName]: value } : orderType
      ),
      error: "",
      message: "",
    }));
  };

  saveOrderType = async (orderType) => {
    const { selectedCompanySlug } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMapCompanyOrderType({
        slug: selectedCompanySlug,
        orderTypeId: orderType.id,
        update: orderType,
      });
      this.setState({
        orderTypes: data.orderTypes || [],
        selectedCompanyProfile: data.organization || this.state.selectedCompanyProfile,
        message: `${orderType.name} gespeichert.`,
      });
      this.loadCompanies();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  submitOrderRequest = (event) => {
    event.preventDefault();
    const project = this.props.currentMapartProject;
    if (!project) {
      this.setState({ error: "Bitte zuerst ein MapArt-Projekt laden oder einstellen.", message: "" });
      return;
    }
    this.findMatchingCompanies();
  };

  loadCustomerOrders = async () => {
    try {
      const data = await listMyMapOrders();
      this.setState({ customerOrders: data.orders || [] });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  cancelCustomerOrder = async (order) => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await cancelMyMapOrder({ orderId: order.id });
      this.setState({
        customerOrders: data.orders || [],
        message: `Auftrag ${order.publicId} wurde storniert.`,
      });
      this.loadCompanyOrders();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  downloadCustomerSchematicZip = async (order) => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      await downloadMyMapOrderSchematicZip({
        orderId: order.id,
        filename: order.schematicFileName || `${order.publicId}.zip`,
      });
      this.setState({ message: "Schematic-ZIP heruntergeladen." });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  downloadCustomerSourceImage = async (order) => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      await downloadMyMapOrderSourceImage({
        orderId: order.id,
        filename: order.imageFileName || `${order.publicId}-bild.png`,
      });
      this.setState({ message: "Bild heruntergeladen." });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  downloadCompanySchematicZip = async (order) => {
    const { selectedCompanySlug } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      await downloadMapCompanyOrderSchematicZip({
        slug: selectedCompanySlug,
        orderId: order.id,
        filename: order.schematicFileName || `${order.publicId}.zip`,
      });
      this.setState({ message: "Schematic-ZIP heruntergeladen." });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  downloadCompanySourceImage = async (order) => {
    const { selectedCompanySlug } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      await downloadMapCompanyOrderSourceImage({
        slug: selectedCompanySlug,
        orderId: order.id,
        filename: order.imageFileName || `${order.publicId}-bild.png`,
      });
      this.setState({ message: "Bild heruntergeladen." });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  setCompanyOrderPayment = async (order, paymentConfirmed) => {
    const { selectedCompanySlug } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMapCompanyOrderPayment({
        slug: selectedCompanySlug,
        orderId: order.id,
        paymentConfirmed,
      });
      this.setState({
        companyOrders: data.orders || [],
        message: paymentConfirmed ? "Zahlung bestaetigt." : "Zahlungsbestaetigung entfernt.",
      });
      await this.loadCustomerOrders();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  setCompanyOrderDiscount = async (order, manualDiscountPerMapPart, manualDiscountNote = order.manualDiscountNote) => {
    const { selectedCompanySlug } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMapCompanyOrderDiscount({
        slug: selectedCompanySlug,
        orderId: order.id,
        manualDiscountPerMapPart,
        manualDiscountNote,
      });
      this.setState({
        companyOrders: data.orders || [],
        message: Number(manualDiscountPerMapPart || 0) > 0 ? "Rabatt gespeichert." : "Rabatt entfernt.",
      });
      await this.loadCustomerOrders();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  loadCompanyOrders = async (slug = this.state.selectedCompanySlug) => {
    if (!slug) return;
    try {
      const data = await listMapCompanyOrders({ slug });
      this.setState({ companyOrders: data.orders || [] });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  loadCompanyMembers = async (slug = this.state.selectedCompanySlug) => {
    if (!slug) return;
    try {
      const data = await listMapCompanyMembers({ slug });
      this.setState({ companyMembers: data.members || [] });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  loadCompanyJoinRequests = async (slug = this.state.selectedCompanySlug) => {
    if (!slug) return;
    try {
      const data = await listMapCompanyJoinRequests({ slug });
      this.setState({ companyJoinRequests: data.joinRequests || [] });
    } catch (error) {
      this.setState({ error: error.message });
    }
  };

  requestCompanyMembership = async (company) => {
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await requestMapCompanyMembership({
        slug: company.slug,
        message: this.state.joinRequestMessage,
      });
      this.setState({
        joinRequestMessage: "",
        myJoinRequests: data.joinRequests || [],
        message: `Anfrage an ${company.name} wurde gesendet.`,
      });
      this.unlockAchievement("first_join_request", { organizationSlug: company.slug });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  resolveCompanyJoinRequest = async (joinRequest, status) => {
    const { selectedCompanySlug } = this.state;
    if (!selectedCompanySlug) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await resolveMapCompanyJoinRequest({
        slug: selectedCompanySlug,
        requestId: joinRequest.id,
        status,
      });
      this.setState({
        companyJoinRequests: data.joinRequests || [],
        companyMembers: data.members || [],
        message: status === "accepted" ? "Mitarbeiter-Anfrage angenommen." : "Mitarbeiter-Anfrage abgelehnt.",
      });
      this.loadCompanies();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  addCompanyMember = async (event) => {
    event.preventDefault();
    const { memberPlayerName, memberRole, selectedCompanySlug } = this.state;
    if (!selectedCompanySlug) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await addMapCompanyMember({
        slug: selectedCompanySlug,
        playerName: memberPlayerName,
        role: memberRole,
      });
      this.setState({
        companyMembers: data.members || [],
        memberPlayerName: "",
        memberRole: "member",
        message: "Mitglied wurde aktualisiert.",
      });
      this.loadCompanies();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  updateCompanyMember = async (member, update) => {
    const { selectedCompanySlug } = this.state;
    if (!selectedCompanySlug) return;

    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMapCompanyMember({
        slug: selectedCompanySlug,
        memberId: member.id,
        role: update.role || member.role,
        active: update.active !== undefined ? update.active : member.active,
      });
      this.setState({
        companyMembers: data.members || [],
        message: "Mitglied gespeichert.",
      });
      this.loadCompanies();
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  findMatchingCompanies = async () => {
    const project = this.props.currentMapartProject;
    if (!project) return;
    this.setState({ loading: true, error: "", message: "", matchingCompanies: [] });
    try {
      const data = await findMatchingMapCompanies({
        category: project.category,
        variant: this.state.orderRequestVariant,
        mapCount: project.mapParts,
        budget: this.state.orderRequestBudget,
      });
      this.setState({
        matchingCompanies: data.candidates || [],
        message: data.candidates?.length
          ? `${data.candidates.length} passende Kartenfirma(en) gefunden.`
          : "Keine Kartenfirma im Budget gefunden. Bitte Budget pruefen oder spaeter erneut versuchen.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  sendOrderToCompany = async (candidate) => {
    const project = this.props.currentMapartProject;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await createMapOrder({
        organizationSlug: candidate.organization.slug,
        orderTypeId: candidate.orderType.id,
        variant: this.state.orderRequestVariant,
        mapCount: project.mapParts,
        budget: this.state.orderRequestBudget,
        notes: this.state.orderRequestNotes,
        project,
      });
      let sourceImageAttached = false;
      if (project.hasUploadedImage && this.props.generateOrderSourceImageFile) {
        const sourceImage = await this.props.generateOrderSourceImageFile(data.order.publicId);
        if (sourceImage) {
          await uploadMyMapOrderSourceImage({ orderId: data.order.id, file: sourceImage });
          sourceImageAttached = true;
        }
      }
      let schematicZipAttached = false;
      if (project.schematicAvailable && this.props.generateOrderSchematicZip) {
        const schematicZip = await this.props.generateOrderSchematicZip(data.order.publicId);
        if (schematicZip) {
          await uploadMyMapOrderSchematicZip({ orderId: data.order.id, file: schematicZip });
          schematicZipAttached = true;
        }
      }
      await this.loadCustomerOrders();
      this.setState({
        matchingCompanies: [],
        message: `Auftrag ${data.order.publicId} wurde an ${candidate.organization.name} gesendet.${
          sourceImageAttached ? " Bild wurde angehaengt." : ""
        }${
          schematicZipAttached ? " Schematic-ZIP wurde angehaengt." : ""
        }`,
      });
      this.unlockAchievement("first_order", { publicId: data.order.publicId });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  setCompanyOrderStatus = async (order, status) => {
    const { selectedCompanySlug } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMapCompanyOrderStatus({
        slug: selectedCompanySlug,
        orderId: order.id,
        status,
        decisionNote: this.getDecisionNoteForStatus(status),
        completedMapCount:
          status === "ready_for_pickup" || status === "completed" ? order.mapCount : order.completedMapCount,
      });
      await this.loadCustomerOrders();
      this.setState({
        companyOrders: data.orders || [],
        message: this.getDecisionNoteForStatus(status),
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  updateCompanyOrderProgress = async (order, completedMapCount) => {
    const { selectedCompanySlug } = this.state;
    this.setState({ loading: true, error: "", message: "" });
    try {
      const data = await updateMapCompanyOrderStatus({
        slug: selectedCompanySlug,
        orderId: order.id,
        status: order.status === "submitted" ? "accepted" : order.status,
        decisionNote: order.decisionNote,
        completedMapCount,
      });
      this.setState({
        companyOrders: data.orders || [],
        message: "Fortschritt gespeichert.",
      });
    } catch (error) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ loading: false });
    }
  };

  formatNumber = (value) => new Intl.NumberFormat("de-DE").format(Number(value || 0));

  getOrderFinalPrice = (order) =>
    Number(order.finalPrice ?? Math.max(0, Number(order.quotedPrice || 0) - Number(order.manualDiscountTotal || 0)));

  formatDateTime = (value) => formatBerlinDateTime(value);

  formatStatus = (status) => {
    const labels = {
      submitted: "Eingereicht - wartet auf Antwort",
      accepted: "Angenommen",
      in_progress: "In Arbeit",
      ready_for_pickup: "Abholbereit",
      completed: "Abgeschlossen",
      declined: "Abgelehnt - bitte andere Firma waehlen",
      cancelled_by_customer: "Vom Kunden storniert",
    };
    return labels[status] || status;
  };

  getDecisionNoteForStatus = (status) => {
    const notes = {
      accepted: "Auftrag angenommen.",
      in_progress: "Auftrag ist in Arbeit.",
      ready_for_pickup: "Auftrag ist abholbereit.",
      completed: "Auftrag abgeschlossen.",
      declined: "Auftrag abgelehnt.",
    };
    return notes[status] || "Status aktualisiert.";
  };

  renderOrderAssetPreview(order, { onDownloadSchematic, onDownloadImage } = {}) {
    const project = order.project || {};
    return (
      <div className="orderAssetPreview">
        {project.previewImageDataUrl ? (
          <button
            type="button"
            className="orderAssetImageButton"
            onClick={() => this.setState({ previewImageModal: { src: project.previewImageDataUrl, title: order.publicId } })}
          >
            <img src={project.previewImageDataUrl} alt={`Vorschau ${order.publicId}`} />
          </button>
        ) : (
          <span className="orderAssetMissing">Keine Bildvorschau gespeichert.</span>
        )}
        <div>
          <b>Auftragsdateien</b>
          <small>
            {order.hasSourceImage
              ? `Bild: ${order.imageFileName || `${order.publicId}-bild.png`}`
              : project.uploadedImageBaseFilename
              ? `Bild geplant: ${project.uploadedImageBaseFilename}`
              : "Bildname nicht gespeichert"}
          </small>
          <small>
            {order.hasSchematicZip
              ? `Schematic-ZIP: ${order.schematicFileName || `${order.publicId}.zip`}`
              : project.schematicFileName
              ? `Schematic geplant: ${project.schematicFileName}`
              : "Keine Schematic-Datei im Auftrag gespeichert."}
          </small>
          {order.hasSchematicZip && onDownloadSchematic ? (
            <button type="button" onClick={() => onDownloadSchematic(order)}>
              Schematic-ZIP herunterladen
            </button>
          ) : null}
          {order.hasSourceImage && onDownloadImage ? (
            <button type="button" onClick={() => onDownloadImage(order)}>
              Bild herunterladen
            </button>
          ) : null}
          {order.discordTicketUrl ? (
            <a href={order.discordTicketUrl} target="_blank" rel="noopener noreferrer">
              Discord-Ticket oeffnen
            </a>
          ) : null}
        </div>
      </div>
    );
  }

  formatVariant = (variant) => (variant === "copy" ? "Kopie" : "Original");

  formatMemberRole = (role) => {
    const labels = {
      owner: "Firmenchef",
      admin: "Admin",
      editor: "Manager",
      member: "Mitglied",
    };
    return labels[role] || role;
  };

  formatJoinRequestStatus = (status) => {
    const labels = {
      pending: "Offen",
      accepted: "Angenommen",
      declined: "Abgelehnt",
      cancelled: "Zurueckgezogen",
    };
    return labels[status] || status;
  };

  formatAvatarReviewStatus = (status) => {
    const labels = {
      none: "Noch kein Profilbild",
      pending: "Wartet auf Admin-Genehmigung",
      approved: "Genehmigt",
      rejected: "Abgelehnt",
    };
    return labels[status] || status || "Noch kein Profilbild";
  };

  getJoinRequestForCompany = (company) =>
    this.state.myJoinRequests.find((joinRequest) => joinRequest.organizationSlug === company.slug);

  markCompanyLogoFailed = (slug) => {
    if (!slug) return;
    this.setState((state) => ({
      companyLogoLoadFailures: {
        ...state.companyLogoLoadFailures,
        [slug]: true,
      },
    }));
  };

  canCancelCustomerOrder = (order) =>
    !["ready_for_pickup", "completed", "declined", "cancelled_by_customer"].includes(order.status);

  renderProfilePreviewModal() {
    const { opsuchtVerification, profileAvatarUrl, profileDisplayName, profileText, user } = this.state;
    if (!user) return null;

    const publicAvatarVisible = Boolean(profileAvatarUrl && user.hasApprovedAvatar);
    return (
      <div
        className="profilePreviewBackdrop"
        role="presentation"
        onClick={() => this.setState({ profilePreviewOpen: false })}
      >
        <div className="profilePreviewModal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
          <div className="profilePreviewHeader">
            <span>
              <b>Profilvorschau</b>
              <small>So wirkt dein Profil fuer andere Nutzer.</small>
            </span>
            <button type="button" onClick={() => this.setState({ profilePreviewOpen: false })}>
              Schliessen
            </button>
          </div>

          <div className="publicProfileCard">
            {publicAvatarVisible ? (
              <img className="publicProfileAvatar" src={profileAvatarUrl} alt={`Profilbild ${profileDisplayName}`} />
            ) : (
              <div className="publicProfileAvatar placeholder">{(profileDisplayName || user.playerName || "?").slice(0, 1)}</div>
            )}
            <div className="publicProfileMain">
              <h3>{profileDisplayName || user.displayName || user.playerName}</h3>
              <small>@{user.playerName}</small>
              <div className="publicProfileBadges">
                <span className={opsuchtVerification?.verified ? "active" : ""}>
                  {opsuchtVerification?.verified ? "OPSUCHT verifiziert" : "OPSUCHT nicht verifiziert"}
                </span>
                <span className={user.discordLinked ? "active" : ""}>
                  {user.discordLinked ? "Discord gekoppelt" : "Discord nicht gekoppelt"}
                </span>
              </div>
              <p>{profileText || "Noch kein Profiltext hinterlegt."}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderDirectoryUserCard(userEntry, { compact = false } = {}) {
    const { directoryAvatarUrls } = this.state;
    const avatarUrl = directoryAvatarUrls[userEntry.id];
    const displayName = userEntry.displayName || userEntry.playerName;

    return (
      <div className={`publicProfileCard ${compact ? "compact" : ""}`}>
        {avatarUrl ? (
          <img className="publicProfileAvatar" src={avatarUrl} alt={`Profilbild ${displayName}`} />
        ) : (
          <div className="publicProfileAvatar placeholder">{(displayName || "?").slice(0, 1)}</div>
        )}
        <div className="publicProfileMain">
          <h3>{displayName}</h3>
          <small>@{userEntry.playerName}</small>
          <div className="publicProfileBadges">
            <span className={userEntry.opsuchtVerified ? "active" : ""}>
              {userEntry.opsuchtVerified ? "OPSUCHT verifiziert" : "OPSUCHT nicht verifiziert"}
            </span>
            <span className={userEntry.discordLinked ? "active" : ""}>
              {userEntry.discordLinked ? "Discord gekoppelt" : "Discord nicht gekoppelt"}
            </span>
          </div>
          <p>{userEntry.profileText || "Noch kein Profiltext hinterlegt."}</p>
        </div>
      </div>
    );
  }

  renderUserDirectoryPanel() {
    const {
      directMessageText,
      directorySearch,
      directorySelectedUserId,
      directoryUsers,
      loading,
      user,
    } = this.state;
    const selectedUser = directoryUsers.find((entry) => String(entry.id) === String(directorySelectedUserId));
    const isSelf = selectedUser && Number(selectedUser.id) === Number(user.id);

    return (
      <div className="userDirectoryPanel">
        <form
          className="userDirectoryToolbar"
          onSubmit={(event) => {
            event.preventDefault();
            this.loadDirectoryUsers();
          }}
        >
          <span>
            <b>Nutzeruebersicht</b>
            <small>
              {loading ? "Lade Profile..." : `${directoryUsers.length} Nutzer geladen`} - Profile ansehen und Direktnachrichten vorbereiten.
            </small>
          </span>
          <input
            value={directorySearch}
            onChange={(event) => this.updateField("directorySearch", event.target.value)}
            placeholder="Ingame-Name suchen"
          />
          <button type="submit" disabled={loading}>
            Suchen
          </button>
          <button type="button" onClick={this.loadDirectoryUsers} disabled={loading}>
            Aktualisieren
          </button>
        </form>

        <div className="userDirectoryLayout">
          <div className="userDirectoryList" role="list">
            {directoryUsers.map((entry) => (
              <button
                type="button"
                role="listitem"
                key={entry.id}
                className={String(entry.id) === String(directorySelectedUserId) ? "active" : ""}
                onClick={() => this.selectDirectoryUser(entry.id)}
              >
                <strong>{entry.displayName || entry.playerName}</strong>
                <small>@{entry.playerName}</small>
              </button>
            ))}
            {!directoryUsers.length ? <p>Keine Nutzer gefunden.</p> : null}
          </div>

          <div className="userDirectoryDetail">
            {selectedUser ? this.renderDirectoryUserCard(selectedUser) : <p>Waehle einen Nutzer aus.</p>}
            {selectedUser ? (
              <div className="directMessageBox">
                <b>DM schreiben</b>
                <textarea
                  value={directMessageText}
                  onChange={(event) => this.updateField("directMessageText", event.target.value)}
                  placeholder={isSelf ? "Du kannst dir nicht selbst schreiben." : `Nachricht an ${selectedUser.playerName}`}
                  rows={5}
                  maxLength={1000}
                  disabled={isSelf}
                />
                <button
                  type="button"
                  onClick={this.sendDirectoryDirectMessage}
                  disabled={loading || isSelf || directMessageText.trim().length < 2}
                >
                  DM senden
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  renderDirectMessagesPanel() {
    const {
      directMessageBox,
      directMessages,
      loading,
      selectedDirectMessageId,
      unreadDirectMessageCount,
    } = this.state;
    const selectedMessage = directMessages.find((entry) => String(entry.id) === String(selectedDirectMessageId));
    const selectedPeer =
      selectedMessage?.direction === "sent" ? selectedMessage?.recipient : selectedMessage?.sender;

    return (
      <div className="directMessagesPanel">
        <div className="userDirectoryToolbar">
          <span>
            <b>Nachrichten</b>
            <small>
              {unreadDirectMessageCount ? `${unreadDirectMessageCount} ungelesen` : "Keine ungelesenen Nachrichten"} - DMs von anderen Nutzern.
            </small>
          </span>
          <div className="segmentedControl">
            <button
              type="button"
              className={directMessageBox === "inbox" ? "active" : ""}
              onClick={() => this.loadDirectMessages("inbox")}
              disabled={loading}
            >
              Posteingang
            </button>
            <button
              type="button"
              className={directMessageBox === "sent" ? "active" : ""}
              onClick={() => this.loadDirectMessages("sent")}
              disabled={loading}
            >
              Gesendet
            </button>
          </div>
          <button type="button" onClick={() => this.loadDirectMessages()} disabled={loading}>
            Aktualisieren
          </button>
        </div>

        <div className="directMessagesLayout">
          <div className="directMessagesList" role="list">
            {directMessages.map((messageEntry) => {
              const peer = messageEntry.direction === "sent" ? messageEntry.recipient : messageEntry.sender;
              const unread = messageEntry.direction === "received" && !messageEntry.readAt;
              return (
                <button
                  type="button"
                  role="listitem"
                  key={messageEntry.id}
                  className={`${String(messageEntry.id) === String(selectedDirectMessageId) ? "active" : ""} ${
                    unread ? "unread" : ""
                  }`}
                  onClick={() => this.selectDirectMessage(messageEntry.id)}
                >
                  <strong>{peer.displayName || peer.playerName}</strong>
                  <small>
                    {messageEntry.direction === "sent" ? "An" : "Von"} @{peer.playerName} - {formatBerlinDateTime(messageEntry.createdAt)}
                  </small>
                  <span>{messageEntry.body}</span>
                </button>
              );
            })}
            {!directMessages.length ? <p>Keine Nachrichten in diesem Bereich.</p> : null}
          </div>

          <div className="directMessageDetail">
            {selectedMessage ? (
              <>
                <div className="directMessageDetailHeader">
                  <span>
                    <b>{selectedPeer?.displayName || selectedPeer?.playerName}</b>
                    <small>
                      {selectedMessage.direction === "sent" ? "Gesendet an" : "Empfangen von"} @{selectedPeer?.playerName}
                    </small>
                  </span>
                  <small>{formatBerlinDateTime(selectedMessage.createdAt)}</small>
                </div>
                <p>{selectedMessage.body}</p>
                {selectedMessage.direction === "received" ? (
                  <small>{selectedMessage.readAt ? `Gelesen: ${formatBerlinDateTime(selectedMessage.readAt)}` : "Ungelesen"}</small>
                ) : null}
              </>
            ) : (
              <p>Waehle eine Nachricht aus.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderFeedbackPanel() {
    const {
      feedbackAffectedArea,
      feedbackAttachment,
      feedbackCategory,
      feedbackDescription,
      feedbackEntries,
      feedbackExpectedBehavior,
      feedbackImpact,
      feedbackReproductionSteps,
      feedbackSubmitting,
      feedbackTitle,
      feedbackActualBehavior,
      loading,
      opsuchtVerification,
      user,
    } = this.state;
    const opsuchtReady = Boolean(opsuchtVerification?.verified || user?.opsuchtVerified);
    const isBugReport = feedbackCategory === "bug";
    const isIdea = feedbackCategory === "idea";

    return (
      <div className="feedbackPanel">
        <div className="feedbackIntro">
          <span>
            <b>Feedback an MapArtCraft</b>
            <small>Fehler, Ideen, Kritik, Lob oder allgemeines Feedback landen direkt im Backend.</small>
          </span>
          <button type="button" onClick={this.loadFeedbackEntries} disabled={loading}>
            Aktualisieren
          </button>
        </div>

        {!user ? (
          <p className="feedbackNotice">Bitte melde dich an, damit du Feedback einreichen kannst.</p>
        ) : !opsuchtReady ? (
          <p className="feedbackNotice">
            Um Feedback einzureichen, musst du deinen OPSUCHT-Spielaccount zunaechst verifizieren. Die Verifizierung findest du unter
            Mein Profil.
          </p>
        ) : null}

        <form className="feedbackForm" onSubmit={this.submitFeedback}>
          <label>
            Kategorie
            <select value={feedbackCategory} onChange={(event) => this.updateField("feedbackCategory", event.target.value)}>
              <option value="bug">Bug-Report</option>
              <option value="idea">Idee</option>
              <option value="criticism">Kritik</option>
              <option value="praise">Lob</option>
              <option value="general">Allgemeines Feedback</option>
            </select>
          </label>
          <label>
            Titel
            <input
              value={feedbackTitle}
              onChange={(event) => this.updateField("feedbackTitle", event.target.value)}
              minLength={4}
              maxLength={120}
              required
              placeholder="Kurz und eindeutig"
            />
          </label>
          <label>
            Betroffene Seite/Funktion
            <input
              value={feedbackAffectedArea}
              onChange={(event) => this.updateField("feedbackAffectedArea", event.target.value)}
              maxLength={220}
              placeholder="z. B. Vorschau, Login, Firmen-Dashboard"
            />
          </label>
          <label className="wide">
            Beschreibung
            <textarea
              value={feedbackDescription}
              onChange={(event) => this.updateField("feedbackDescription", event.target.value)}
              minLength={12}
              maxLength={5000}
              required
              rows={5}
              placeholder="Was ist passiert oder was wuenschst du dir?"
            />
          </label>

          {isBugReport ? (
            <>
              <label className="wide">
                Schritte zur Reproduktion
                <textarea
                  value={feedbackReproductionSteps}
                  onChange={(event) => this.updateField("feedbackReproductionSteps", event.target.value)}
                  rows={3}
                  placeholder="1. ... 2. ... 3. ..."
                />
              </label>
              <label>
                Erwartetes Verhalten
                <textarea
                  value={feedbackExpectedBehavior}
                  onChange={(event) => this.updateField("feedbackExpectedBehavior", event.target.value)}
                  rows={3}
                />
              </label>
              <label>
                Tatsaechliches Verhalten
                <textarea
                  value={feedbackActualBehavior}
                  onChange={(event) => this.updateField("feedbackActualBehavior", event.target.value)}
                  rows={3}
                />
              </label>
            </>
          ) : null}

          {isIdea ? (
            <label className="wide">
              Wem hilft diese Idee?
              <textarea
                value={feedbackImpact}
                onChange={(event) => this.updateField("feedbackImpact", event.target.value)}
                rows={3}
                placeholder="Kunden, Kartenfirmen, Admins, Discord-Nutzer ..."
              />
            </label>
          ) : null}

          <label>
            Screenshot optional
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={this.updateFeedbackAttachment} />
            <small>{feedbackAttachment ? feedbackAttachment.name : "PNG, JPG oder WEBP bis 3 MB"}</small>
          </label>

          <label className="feedbackConsent">
            <input type="checkbox" required />
            Ich stimme zu, dieses Feedback an das MapArtCraft-Team zu senden.
          </label>

          <button type="submit" disabled={!user || !opsuchtReady || feedbackSubmitting || loading}>
            Feedback einreichen
          </button>
        </form>

        <div className="feedbackList">
          <b>Meine eingereichten Meldungen</b>
          {feedbackEntries.length ? (
            feedbackEntries.map((entry) => (
              <article className="feedbackCard" key={entry.id}>
                <span>
                  <strong>{entry.publicId}</strong>
                  <small>
                    {entry.categoryLabel} - {formatBerlinDateTime(entry.createdAt)}
                  </small>
                </span>
                <h3>{entry.title}</h3>
                <p>{entry.description}</p>
                <div className="feedbackMeta">
                  <span>Status: {entry.statusLabel}</span>
                  <span>Aktualisiert: {formatBerlinDateTime(entry.updatedAt)}</span>
                  {entry.publicResponse ? <span>Antwort: {entry.publicResponse}</span> : null}
                  {entry.attachmentCount ? <span>{entry.attachmentCount} Anhang</span> : null}
                </div>
              </article>
            ))
          ) : (
            <p>Noch kein Feedback eingereicht.</p>
          )}
        </div>
      </div>
    );
  }

  renderAchievementsPanel() {
    const unlockedById = new Map(this.state.achievements.map((achievement) => [achievement.id, achievement]));
    const unlockedCount = ACHIEVEMENTS.filter((achievement) => unlockedById.has(achievement.id)).length;
    const secretCount = ACHIEVEMENTS.filter((achievement) => achievement.secret && !unlockedById.has(achievement.id)).length;

    return (
      <div className="userProfileCard achievementPanel">
        <span>
          <b>Erfolge</b>
          <small>
            {unlockedCount} / {ACHIEVEMENTS.length} freigeschaltet{secretCount ? ` - ${secretCount} verborgen` : ""}
          </small>
        </span>
        <div className="achievementGrid">
          {ACHIEVEMENTS.map((achievement) => {
            const unlocked = unlockedById.get(achievement.id);
            const hidden = achievement.secret && !unlocked;
            return (
              <div className={`achievementCard ${unlocked ? "unlocked" : "locked"} ${achievement.secret ? "secret" : ""}`} key={achievement.id}>
                <strong>{hidden ? "???" : achievement.title}</strong>
                <small>{hidden ? "Ein verborgenes Easter Egg wartet noch darauf, gefunden zu werden." : achievement.description}</small>
                <span>{unlocked ? `Freigeschaltet: ${formatBerlinDateTime(unlocked.unlockedAt)}` : "Noch offen"}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderUserProfilePanel() {
    const {
      currentPassword,
      discordLinkCode,
      loading,
      newPassword,
      newPasswordConfirm,
      profileDisplayName,
      profileAvatarUrl,
      profileText,
      opsuchtVerification,
      twoFactorDisableCode,
      twoFactorQrCodeDataUrl,
      twoFactorSetup,
      twoFactorSetupCode,
      user,
    } = this.state;

    return (
      <div className="userProfilePanel">
        <div className="userProfileCard">
          <span>
            <b>Eigenes Profil</b>
            <small>{user.twoFactorEnabled ? "2FA aktiv" : "2FA nicht aktiv"}</small>
          </span>
          <label>
            Anzeigename
            <input
              value={profileDisplayName}
              readOnly
              maxLength={32}
            />
            <small>Der Anzeigename folgt deinem Ingame-Namen und kann nur ueber die Namensaenderungspruefung angepasst werden.</small>
          </label>
          <label className="wide">
            Profiltext
            <textarea
              value={profileText}
              onChange={(event) => this.updateField("profileText", event.target.value)}
              maxLength={500}
              rows={3}
            />
          </label>
          <button type="button" onClick={this.saveMyProfile} disabled={loading}>
            Profil speichern
          </button>
          <button type="button" onClick={this.openProfilePreview}>
            Profilvorschau
          </button>
        </div>

        <div className="userProfileCard">
          <span>
            <b>Passwort aendern</b>
            <small>Nach einem Admin-Reset nutzt du hier das temporaere Passwort als aktuelles Passwort.</small>
          </span>
          <label>
            Aktuelles Passwort
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => this.updateField("currentPassword", event.target.value)}
              autoComplete="current-password"
            />
          </label>
          <label>
            Neues Passwort
            <input
              type="password"
              value={newPassword}
              onChange={(event) => this.updateField("newPassword", event.target.value)}
              autoComplete="new-password"
              minLength={8}
            />
          </label>
          <label>
            Neues Passwort wiederholen
            <input
              type="password"
              value={newPasswordConfirm}
              onChange={(event) => this.updateField("newPasswordConfirm", event.target.value)}
              autoComplete="new-password"
              minLength={8}
            />
          </label>
          <button
            type="button"
            onClick={this.saveMyPassword}
            disabled={loading || !currentPassword || !newPassword || !newPasswordConfirm}
          >
            Passwort speichern
          </button>
        </div>

        <div className="userProfileCard">
          <b>OPSUCHT-Verifizierung</b>
          <div className="opsuchtVerificationStatus">
            <strong>{opsuchtVerification?.verified ? "OPSUCHT verifiziert" : "Nicht OPSUCHT verifiziert"}</strong>
            <small>
              {opsuchtVerification?.verifiedAt
                ? `Verifiziert am ${this.formatDateTime(opsuchtVerification.verifiedAt)} Uhr`
                : opsuchtVerification?.lastError || "Bestaetige, dass dieser Ingame-Name wirklich dir gehoert."}
            </small>
            {opsuchtVerification?.minecraftUuid ? <code>{opsuchtVerification.minecraftUuid}</code> : null}
          </div>
          {opsuchtVerification?.challenge ? (
            <div className="opsuchtVerificationChallenge">
              <small>1. Benenne auf OPSUCHT ein Blatt Papier exakt so:</small>
              <code>{opsuchtVerification.challenge.code}</code>
              <small>
                2. Stelle dieses Papier moeglichst fuer {this.formatNumber(opsuchtVerification.challenge.symbolicPrice)} Dollar ins
                Auktionshaus. Gebote oder ein anderer aktueller Preis sind fuer die Pruefung egal.
              </small>
              <small>Gueltig bis {this.formatDateTime(opsuchtVerification.challenge.expiresAt)} Uhr</small>
              <button type="button" onClick={this.checkOpsuchtVerification} disabled={loading}>
                OPSUCHT jetzt pruefen
              </button>
            </div>
          ) : (
            <button type="button" onClick={this.startOpsuchtVerification} disabled={loading || opsuchtVerification?.verified}>
              OPSUCHT verifizieren
            </button>
          )}
          <button
            type="button"
            onClick={this.prepareNameChangeCheck}
            disabled={loading || !opsuchtVerification?.minecraftUuid}
          >
            Namensaenderung pruefen
          </button>
        </div>

        <div className="userProfileCard">
          <b>Profilbild</b>
          <div className="profileAvatarBox">
            {profileAvatarUrl ? (
              <img className="profileAvatarPreview" src={profileAvatarUrl} alt="Eigenes Profilbild" />
            ) : (
              <div className="profileAvatarPlaceholder">Kein Bild</div>
            )}
            <span>
              <small>Status</small>
              <b>{this.formatAvatarReviewStatus(user.avatarReviewStatus)}</b>
            </span>
          </div>
          <p>
            Bilder werden automatisch verkleinert und bleiben ohne Ablauf in Pruefung. Sichtbar wird ein Profilbild erst
            nach Admin-Genehmigung. Automatische NSFW-Erkennung wird spaeter als Moderationsdienst angebunden.
          </p>
          <label>
            Bild hochladen
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={this.uploadMyAvatar} disabled={loading} />
          </label>
        </div>

        <div className="userProfileCard">
          <b>2FA</b>
          {user.twoFactorEnabled ? (
            <React.Fragment>
              <label>
                Code zum Deaktivieren
                <input
                  type="text"
                  inputMode="numeric"
                  value={twoFactorDisableCode}
                  onChange={(event) => this.updateField("twoFactorDisableCode", event.target.value)}
                  maxLength={6}
                />
              </label>
              <button type="button" onClick={this.disableTwoFactorForAccount} disabled={loading || !twoFactorDisableCode}>
                2FA deaktivieren
              </button>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <button type="button" onClick={this.startTwoFactorSetup} disabled={loading}>
                2FA vorbereiten
              </button>
              {twoFactorSetup ? (
                <div className="twoFactorSetupBox">
                  {twoFactorQrCodeDataUrl ? (
                    <img className="twoFactorQrCode" src={twoFactorQrCodeDataUrl} alt="2FA QR-Code zum Scannen" />
                  ) : null}
                  <small>Secret fuer Authenticator-App:</small>
                  <code>{twoFactorSetup.secret}</code>
                  <small>{twoFactorSetup.otpAuthUri}</small>
                  <label>
                    Code bestaetigen
                    <input
                      type="text"
                      inputMode="numeric"
                      value={twoFactorSetupCode}
                      onChange={(event) => this.updateField("twoFactorSetupCode", event.target.value)}
                      maxLength={6}
                    />
                  </label>
                  <button type="button" onClick={this.enableTwoFactorForAccount} disabled={loading || !twoFactorSetupCode}>
                    2FA aktivieren
                  </button>
                </div>
              ) : null}
            </React.Fragment>
          )}
        </div>

        <div className="userProfileCard">
          <b>Discord koppeln</b>
          <p>Vorbereitung fuer den Bot: Der Code wird spaeter im Discord-Bot bestaetigt.</p>
          <button type="button" onClick={this.prepareDiscordLink} disabled={loading}>
            Kopplungscode erstellen
          </button>
          {discordLinkCode ? (
            <code>
              {discordLinkCode.code} - gueltig bis {discordLinkCode.expiresAt}
            </code>
          ) : null}
        </div>

        {this.renderAchievementsPanel()}
      </div>
    );
  }

  isArchivedCompanyOrder = (order) =>
    order.status === "cancelled_by_customer" || (order.status === "completed" && order.paymentConfirmed);

  renderCompanyOrderCard(order) {
    const { loading } = this.state;
    const isArchived = this.isArchivedCompanyOrder(order);
    const finalPrice = this.getOrderFinalPrice(order);
    const hasDiscount = Number(order.manualDiscountPerMapPart || 0) > 0;

    return (
      <div className={`companyOrderCard ${isArchived ? "archived" : ""}`} key={order.id}>
        <span className="companyOrderIdentity">
          <b>{order.publicId}</b>
          <small>Kunde: {order.customerIngameName}</small>
          <small>
            {order.orderTypeName} - {this.formatVariant(order.variant)} - {order.mapCount} Karten
          </small>
        </span>
        <span className="companyOrderMeta">
          <b>{this.formatNumber(finalPrice)}</b>
          <small>Original: {this.formatNumber(order.quotedPrice)}</small>
          {hasDiscount ? (
            <small>
              Rabatt: {this.formatNumber(order.manualDiscountPerMapPart)} pro Karte / {this.formatNumber(order.manualDiscountTotal)} gesamt
            </small>
          ) : (
            <small>Kein Rabatt gesetzt</small>
          )}
          <small>Status: {this.formatStatus(order.status)}</small>
          <small>
            {order.claimedByPlayerName ? `Bearbeiter: ${order.claimedByPlayerName}` : "Noch nicht angenommen"}
          </small>
          <small>
            {order.paymentConfirmed
              ? `Bezahlt${order.paymentConfirmedByPlayerName ? ` - ${order.paymentConfirmedByPlayerName}` : ""}`
              : "Nicht als bezahlt markiert"}
          </small>
        </span>
        <p>{order.notes || "Keine Notizen."}</p>
        {this.renderOrderAssetPreview(order, {
          onDownloadSchematic: this.downloadCompanySchematicZip,
          onDownloadImage: this.downloadCompanySourceImage,
        })}
        <form
          className="companyOrderDiscount"
          onSubmit={(event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            this.setCompanyOrderDiscount(order, formData.get("manualDiscountPerMapPart"), formData.get("manualDiscountNote"));
          }}
        >
          <label>
            Rabatt pro Kartenteil
            <input
              name="manualDiscountPerMapPart"
              type="number"
              min="0"
              step="1000"
              defaultValue={order.manualDiscountPerMapPart || 0}
              disabled={loading || ["declined", "cancelled_by_customer", "completed"].includes(order.status)}
              placeholder="z. B. 50000"
            />
          </label>
          <label>
            Rabatt-Hinweis
            <input
              name="manualDiscountNote"
              type="text"
              maxLength={500}
              defaultValue={order.manualDiscountNote || ""}
              disabled={loading || ["declined", "cancelled_by_customer", "completed"].includes(order.status)}
              placeholder="z. B. Kartenstuemmer-Rabatt"
            />
          </label>
          <button type="submit" disabled={loading || ["declined", "cancelled_by_customer", "completed"].includes(order.status)}>
            Rabatt speichern
          </button>
        </form>
        <div className="companyOrderProgress">
          <label>
            Fertige Kartenteile
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={order.completedMapCount || 0}
              onChange={(event) => this.updateCompanyOrderProgress(order, event.target.value)}
              disabled={loading || ["declined", "cancelled_by_customer", "completed"].includes(order.status)}
            />
          </label>
          <span>
            {this.formatNumber(order.completedMapCount)} / {this.formatNumber(order.mapCount)} fertig
          </span>
        </div>
        <div className="actions">
          <button
            type="button"
            onClick={() => this.setCompanyOrderStatus(order, "accepted")}
            disabled={loading || order.status === "cancelled_by_customer"}
          >
            Annehmen
          </button>
          <button
            type="button"
            onClick={() => this.setCompanyOrderStatus(order, "in_progress")}
            disabled={loading || order.status === "cancelled_by_customer"}
          >
            In Arbeit
          </button>
          <button
            type="button"
            onClick={() => this.setCompanyOrderStatus(order, "ready_for_pickup")}
            disabled={loading || order.status === "cancelled_by_customer"}
          >
            Abholbereit
          </button>
          <button
            type="button"
            onClick={() => this.setCompanyOrderStatus(order, "completed")}
            disabled={loading || order.status === "cancelled_by_customer"}
          >
            Abgeschlossen
          </button>
          <button
            type="button"
            onClick={() => this.setCompanyOrderStatus(order, "declined")}
            disabled={loading || order.status === "cancelled_by_customer"}
          >
            Ablehnen
          </button>
          <button
            type="button"
            onClick={() => this.setCompanyOrderPayment(order, !order.paymentConfirmed)}
            disabled={loading || ["declined", "cancelled_by_customer"].includes(order.status)}
          >
            {order.paymentConfirmed ? "Zahlung entfernen" : "Als bezahlt markieren"}
          </button>
        </div>
      </div>
    );
  }

  renderOrderRequestForm() {
    const { customerOrders, loading, matchingCompanies, orderRequestBudget, orderRequestNotes, orderRequestVariant } = this.state;
    const project = this.props.currentMapartProject;
    const mapParts = project?.mapParts || 0;
    const examplePrice = mapParts * 400000;

    return (
      <form className="orderRequestPanel" onSubmit={this.submitOrderRequest}>
        <div className="orderRequestIntro">
          <b>MapArt-Auftrag erstellen</b>
          <small>Grunddaten fuer spaeteres Matching nach Kategorie, Budget und Firmenpreisen.</small>
        </div>

        <div className="orderProjectSummary">
          <span>
            <b>{project?.category || "Kein MapArt-Projekt"}</b>
            <small>
              {project
                ? `${project.mapSizeX}x${project.mapSizeY} = ${project.mapParts} Kartenteile · ${project.mode} · ${project.version}`
                : "Unten im Generator Bild laden und Map Size einstellen."}
            </small>
          </span>
          <span>
            <b>{project?.hasUploadedImage ? "Bild erkannt" : "Kein Bild"}</b>
            <small>{project?.schematicAvailable ? "Schematic kann aus dem aktuellen Setup erzeugt werden." : "Aktuell map.dat-Modus."}</small>
          </span>
        </div>

        <label>
          Art
          <select
            value={orderRequestVariant}
            onChange={(event) => this.updateField("orderRequestVariant", event.target.value)}
          >
            <option value="original">Original</option>
            <option value="copy">Kopie</option>
          </select>
        </label>

        <label>
          Budget
          <input
            type="number"
            min="0"
            value={orderRequestBudget}
            onChange={(event) => this.updateField("orderRequestBudget", event.target.value)}
            placeholder="z. B. 1200000"
          />
        </label>

        <div className="orderPricePreview">
          <b>Beispielrechnung</b>
          <small>
            {project
              ? `${this.formatNumber(mapParts)} x 400.000 = ${this.formatNumber(examplePrice)}`
              : "Wird automatisch berechnet, sobald ein Projekt vorhanden ist."}
          </small>
        </div>

        <label className="wide">
          Notizen
          <textarea
            value={orderRequestNotes}
            onChange={(event) => this.updateField("orderRequestNotes", event.target.value)}
            placeholder="Motiv, Stil, Wunsch-CB, Deadline oder besondere Hinweise"
            rows={3}
          />
        </label>

        <button type="submit" disabled={loading}>
          Passende Firmen suchen
        </button>

        {matchingCompanies.length ? (
          <div className="orderMatchGrid">
            {matchingCompanies.map((candidate) => (
              <div className="orderMatchCard" key={`${candidate.organization.slug}-${candidate.orderType.id}`}>
                <b>{candidate.organization.name}</b>
                <small>{candidate.organization.cityBuild || "CB offen"} · {candidate.orderType.name}</small>
                <span>{this.formatNumber(candidate.pricePerMapPart)} pro Karte</span>
                <strong>{this.formatNumber(candidate.totalPrice)} gesamt</strong>
                <button type="button" onClick={() => this.sendOrderToCompany(candidate)} disabled={loading}>
                  An {candidate.organization.name} senden
                </button>
              </div>
            ))}
          </div>
        ) : null}

        {false && customerOrders.length ? (
          <div className="orderStatusList">
            <b>Meine Auftraege</b>
            {customerOrders.map((order) => (
              <div className="orderStatusItem" key={order.id}>
                <span>
                  <strong>{order.publicId}</strong> · {order.organizationName} · {order.orderTypeName}
                </span>
                <small>{order.status} · {this.formatNumber(order.quotedPrice)}</small>
              </div>
            ))}
          </div>
        ) : null}
      </form>
    );
  }

  renderCustomerOrdersPanel() {
    const { customerOrders, loading } = this.state;

    return (
      <div className="orderCustomerOrdersPanel">
        <div className="orderProviderPricingHeader">
          <span>
            <b>Meine Auftraege</b>
            <small>Status deiner eingesendeten MapArt-Auftraege.</small>
          </span>
          <button type="button" onClick={this.loadCustomerOrders} disabled={loading}>
            Aktualisieren
          </button>
        </div>

        {customerOrders.length ? (
          <div className="orderStatusList">
            {customerOrders.map((order) => (
              <div className={`orderStatusItem ${order.status}`} key={order.id}>
                <span>
                  <strong>{order.publicId}</strong>
                  <small>
                    {order.organizationName} - {order.orderTypeName} - {this.formatVariant(order.variant)} -{" "}
                    {this.formatNumber(order.mapCount)} Karten
                  </small>
                </span>
                {this.renderOrderAssetPreview(order, {
                  onDownloadSchematic: this.downloadCustomerSchematicZip,
                  onDownloadImage: this.downloadCustomerSourceImage,
                })}
                <span>
                  <b>{this.formatStatus(order.status)}</b>
                  <small>{this.formatNumber(this.getOrderFinalPrice(order))} zu zahlen</small>
                  {Number(order.manualDiscountPerMapPart || 0) > 0 ? (
                    <small>
                      Rabatt: {this.formatNumber(order.manualDiscountPerMapPart)} pro Kartenteil,{" "}
                      {this.formatNumber(order.manualDiscountTotal)} gesamt
                    </small>
                  ) : (
                    <small>Kein Rabatt gesetzt</small>
                  )}
                  <small>
                    {order.claimedByPlayerName
                      ? `Bearbeitet von ${order.claimedByPlayerName}`
                      : "Noch kein Kartenbauer zugeordnet"}
                  </small>
                  <small>
                    Fortschritt: {this.formatNumber(order.completedMapCount)} / {this.formatNumber(order.mapCount)} Kartenteile
                  </small>
                  <small>
                    {order.paymentConfirmed
                      ? `Bezahlt${order.paymentConfirmedByPlayerName ? ` - bestaetigt von ${order.paymentConfirmedByPlayerName}` : ""}`
                      : "Zahlung noch nicht bestaetigt"}
                  </small>
                </span>
                {order.status === "declined" ? (
                  <p>Diese Firma hat abgelehnt. Bitte suche eine andere passende Kartenfirma oder versuche es spaeter erneut.</p>
                ) : null}
                {order.status === "cancelled_by_customer" ? <p>Du hast diesen Auftrag storniert.</p> : null}
                {order.decisionNote ? <p>{order.decisionNote}</p> : null}
                {this.canCancelCustomerOrder(order) ? (
                  <div className="actions">
                    <button type="button" onClick={() => this.cancelCustomerOrder(order)} disabled={loading}>
                      Auftrag stornieren
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="orderProviderEmpty">Noch keine Auftraege eingereicht.</p>
        )}
      </div>
    );
  }

  renderCompanyDiscoveryPanel() {
    const { companies, companyLogoLoadFailures, joinRequestMessage, loading, publicCompanies } = this.state;
    const ownCompanySlugs = new Set(companies.map((company) => company.slug));

    return (
      <div className="companyDiscoveryPanel">
        <div className="orderProviderPricingHeader">
          <span>
            <b>Kartenfirmen finden</b>
            <small>Bewirb dich bei einer Kartenfirma, wenn du dort als Kartenbauer mitarbeiten willst.</small>
          </span>
          <button type="button" onClick={() => { this.loadPublicCompanies(); this.loadMyJoinRequests(); }} disabled={loading}>
            Aktualisieren
          </button>
        </div>

        <label className="wide">
          Kurze Nachricht optional
          <textarea
            value={joinRequestMessage}
            onChange={(event) => this.updateField("joinRequestMessage", event.target.value)}
            placeholder="z. B. Ich bin im Clan und moechte als Kartenbauer mitarbeiten."
            rows={2}
          />
        </label>

        <div className="companyDiscoveryGrid">
          {publicCompanies.length ? (
            publicCompanies.map((company) => {
              const joinRequest = this.getJoinRequestForCompany(company);
              const isOwnCompany = ownCompanySlugs.has(company.slug);
              const shouldShowLogo =
                (company.hasLogo || company.logoReviewStatus === "approved" || company.logoUrl) && !companyLogoLoadFailures[company.slug];
              return (
                <div className="companyDiscoveryCard" key={company.slug}>
                  <div className="companyDiscoveryHeader">
                    <div className="companyDiscoveryLogo">
                      {shouldShowLogo ? (
                        <img
                          src={`${getMapCompanyLogoUrl({ slug: company.slug })}?v=${encodeURIComponent(company.updatedAt || "")}`}
                          alt={`Logo ${company.name}`}
                          onError={() => this.markCompanyLogoFailed(company.slug)}
                        />
                      ) : (
                        <span>{(company.ticketPrefix || company.name || "?").slice(0, 4)}</span>
                      )}
                    </div>
                    <span>
                      <b>{company.name}</b>
                      <small>
                        {company.ticketPrefix ? `${company.ticketPrefix} - ` : ""}
                        {company.projectGuidelines || "CB offen"} - {company.acceptingOrders ? "nimmt Auftraege an" : "pausiert"}
                      </small>
                    </span>
                  </div>
                  <p>{company.description || company.profileText || "Keine Beschreibung hinterlegt."}</p>
                  {joinRequest ? (
                    <strong>Anfrage: {this.formatJoinRequestStatus(joinRequest.status)}</strong>
                  ) : isOwnCompany ? (
                    <strong>Du bist bereits in dieser Firma.</strong>
                  ) : (
                    <button type="button" onClick={() => this.requestCompanyMembership(company)} disabled={loading}>
                      Mitarbeit anfragen
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <p className="orderProviderEmpty">Noch keine oeffentlichen Kartenfirmen gefunden.</p>
          )}
        </div>
      </div>
    );
  }

  renderProviderDashboard() {
    const {
      availableCityBuilds,
      companies,
      companyMembers,
      companyJoinRequests,
      companyDiscordGuilds,
      companyDiscordInviteUrl,
      companyDiscordRequiredPermissions,
      companyDiscordSettings,
      companyOrders,
      loading,
      memberPlayerName,
      memberRole,
      orderTypes,
      providerDashboardTab,
      selectedCompanyProfile,
      selectedCompanySlug,
    } = this.state;
    if (!companies.length) return null;
    const selectedDiscordGuild = companyDiscordGuilds.find((guild) => guild.id === companyDiscordSettings?.guildId);
    const discordTargets = selectedDiscordGuild?.targets || [];
    const selectedCompanyIsOwner = selectedCompanyProfile?.memberRole === "owner";

    return (
      <div className="orderProviderDashboard">
        <div className="orderProviderHeader">
          <span>
            <b>Anbieter-Dashboard</b>
            <small>Profil, Angebot und Preise deiner Kartenfirma</small>
          </span>
          <select value={selectedCompanySlug} onChange={(event) => this.selectCompany(event.target.value)}>
            {companies.map((company) => (
              <option key={company.slug} value={company.slug}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="orderProviderTabs" role="tablist" aria-label="Firmen-Dashboard Bereiche">
          <button
            type="button"
            className={providerDashboardTab === "orders" ? "active" : ""}
            onClick={() => this.setProviderDashboardTab("orders")}
          >
            Auftragseingang
          </button>
          <button
            type="button"
            className={providerDashboardTab === "profile" ? "active" : ""}
            onClick={() => this.setProviderDashboardTab("profile")}
          >
            Profil & Preise
          </button>
          <button
            type="button"
            className={providerDashboardTab === "members" ? "active" : ""}
            onClick={() => this.setProviderDashboardTab("members")}
          >
            Mitglieder
          </button>
          <button
            type="button"
            className={providerDashboardTab === "discord" ? "active" : ""}
            onClick={() => this.setProviderDashboardTab("discord")}
          >
            Discord
          </button>
        </div>

        {providerDashboardTab === "profile" && selectedCompanyProfile ? (
          <form className="orderProviderProfileGrid" onSubmit={this.saveSelectedCompanyProfile}>
            <div className="companyLogoUpload wide">
              <div>
                {selectedCompanyProfile.hasLogo || selectedCompanyProfile.logoReviewStatus === "pending" ? (
                  <img
                    src={`${getMyMapCompanyLogoUrl({ slug: selectedCompanyProfile.slug })}?v=${encodeURIComponent(
                      selectedCompanyProfile.updatedAt || ""
                    )}`}
                    alt={`Logo ${selectedCompanyProfile.name}`}
                  />
                ) : (
                  <span>{(selectedCompanyProfile.ticketPrefix || selectedCompanyProfile.name || "?").slice(0, 4)}</span>
                )}
              </div>
              <label>
                Firmenlogo
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={this.uploadSelectedCompanyLogo}
                  disabled={loading}
                />
                <small>
                  PNG, JPG oder WEBP. Status:{" "}
                  {selectedCompanyProfile.logoReviewStatus === "approved"
                    ? "genehmigt"
                    : selectedCompanyProfile.logoReviewStatus === "pending"
                      ? "wartet auf Admin-Genehmigung"
                      : selectedCompanyProfile.logoReviewStatus === "rejected"
                        ? "abgelehnt"
                        : "kein Logo"}
                </small>
              </label>
            </div>
            <label className="wide">
              Beschreibung
              <textarea
                value={selectedCompanyProfile.description || ""}
                onChange={(event) => this.updateProfileField("description", event.target.value)}
                rows={2}
              />
            </label>
            <label>
              Kuerzel
              <input
                value={selectedCompanyProfile.ticketPrefix || ""}
                onChange={(event) => this.updateProfileField("ticketPrefix", event.target.value.toUpperCase())}
                placeholder="z. B. PHCO"
                maxLength={8}
              />
            </label>
            <label className="wide">
              Profiltext
              <textarea
                value={selectedCompanyProfile.profileText || ""}
                onChange={(event) => this.updateProfileField("profileText", event.target.value)}
                placeholder="Was macht eure Kartenfirma aus?"
                rows={3}
              />
            </label>
            <div className="cityBuildField">
              CB
              <div className="cityBuildPicker" aria-label="CityBuild auswaehlen">
                {availableCityBuilds.map((cityBuild) => (
                  <button
                    type="button"
                    draggable="true"
                    className={selectedCompanyProfile.projectGuidelines === cityBuild ? "selected" : ""}
                    key={cityBuild}
                    onClick={() => this.updateProfileField("projectGuidelines", cityBuild)}
                  >
                    {cityBuild}
                  </button>
                ))}
                {!availableCityBuilds.length ? <small>Keine CBs freigeschaltet.</small> : null}
              </div>
            </div>
            <label>
              Swap
              <textarea
                value={selectedCompanyProfile.swapPolicy || ""}
                onChange={(event) => this.updateProfileField("swapPolicy", event.target.value)}
                rows={2}
              />
            </label>
            <label>
              Discord-Link
              <input
                value={selectedCompanyProfile.discordUrl || ""}
                onChange={(event) => this.updateProfileField("discordUrl", event.target.value)}
                placeholder="https://discord.gg/..."
              />
            </label>
            <label>
              Webseite
              <input
                value={selectedCompanyProfile.websiteUrl || ""}
                onChange={(event) => this.updateProfileField("websiteUrl", event.target.value)}
                placeholder="https://..."
              />
            </label>
            <button type="submit" disabled={loading}>
              Profil speichern
            </button>
          </form>
        ) : null}

        {providerDashboardTab === "profile" && selectedCompanyProfile && selectedCompanyIsOwner ? (
          <div className="companyDangerZone">
            <span>
              <b>Kartenfirma loeschen</b>
              <small>
                Nur moeglich, solange keine Auftragstypen oder Auftraege an dieser Firma haengen. Zur Sicherheit musst du
                den Firmennamen exakt bestaetigen.
              </small>
            </span>
            <button type="button" className="danger" onClick={this.deleteSelectedCompany} disabled={loading}>
              Firma loeschen
            </button>
          </div>
        ) : null}

        {providerDashboardTab === "profile" ? (
          <React.Fragment>
            <div className="orderProviderPricingHeader">
              <span>
                <b>Angebot & Preise</b>
                <small>Aktivierte Kategorien sind spaeter fuer Kunden sichtbar und filterbar.</small>
              </span>
              <button type="button" onClick={this.createDefaultTypes} disabled={loading}>
                Standardtypen anlegen
              </button>
            </div>

            <div className="orderProviderTypeGrid">
              {orderTypes.map((orderType) => (
                <div className="orderProviderTypeCard" key={orderType.id}>
                  <label className="orderProviderToggle">
                    <input
                      type="checkbox"
                      checked={orderType.active}
                      onChange={(event) => this.updateOrderTypeField(orderType.id, "active", event.target.checked)}
                    />
                    <span>{orderType.name}</span>
                  </label>
                  <label>
                    Original pro Karte
                    <input
                      type="number"
                      min="0"
                      value={orderType.pricePerOriginalMapPart || 0}
                      onChange={(event) =>
                        this.updateOrderTypeField(orderType.id, "pricePerOriginalMapPart", event.target.value)
                      }
                    />
                  </label>
                  <label>
                    Kopie pro Karte
                    <input
                      type="number"
                      min="0"
                      value={orderType.pricePerCopyMapPart || 0}
                      onChange={(event) => this.updateOrderTypeField(orderType.id, "pricePerCopyMapPart", event.target.value)}
                    />
                  </label>
                  <label>
                    Kurztext
                    <textarea
                      value={orderType.description || ""}
                      onChange={(event) => this.updateOrderTypeField(orderType.id, "description", event.target.value)}
                      rows={2}
                    />
                  </label>
                  <button type="button" onClick={() => this.saveOrderType(orderType)} disabled={loading}>
                    Speichern
                  </button>
                </div>
              ))}
              {!orderTypes.length ? <p className="orderProviderEmpty">Noch keine Auftragstypen angelegt.</p> : null}
            </div>
          </React.Fragment>
        ) : null}

        {providerDashboardTab === "orders" ? (
          <div className="companyOrderInbox">
          <div className="orderProviderPricingHeader">
            <span>
              <b>Auftragseingang</b>
              <small>Eingesendete Kundenauftraege fuer diese Kartenfirma.</small>
            </span>
            <button type="button" onClick={() => this.loadCompanyOrders()} disabled={loading}>
              Aktualisieren
            </button>
          </div>
          {companyOrders.length ? (
            (() => {
              const activeOrders = companyOrders.filter((order) => !this.isArchivedCompanyOrder(order));
              const archivedOrders = companyOrders.filter((order) => this.isArchivedCompanyOrder(order));

              return (
                <React.Fragment>
                  {activeOrders.length ? (
                    activeOrders.map((order) => this.renderCompanyOrderCard(order))
                  ) : (
                    <p className="orderProviderEmpty">Keine aktiven Auftraege.</p>
                  )}

                  {archivedOrders.length ? (
                    <div className={`companyOrderArchive ${this.state.companyArchiveExpanded ? "expanded" : "collapsed"}`}>
                      <button
                        type="button"
                        className="companyOrderArchiveToggle"
                        onClick={() =>
                          this.setState((state) => ({ companyArchiveExpanded: !state.companyArchiveExpanded }))
                        }
                        aria-expanded={this.state.companyArchiveExpanded}
                      >
                        <span>
                          <b>Auftragsarchiv</b>
                          <small>{archivedOrders.length} erledigte oder stornierte Auftraege.</small>
                        </span>
                        <strong>{this.state.companyArchiveExpanded ? "Zuklappen" : "Aufklappen"}</strong>
                      </button>
                      {this.state.companyArchiveExpanded ? (
                        <div className="companyOrderArchiveList">
                          {archivedOrders.map((order) => this.renderCompanyOrderCard(order))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })()
          ) : (
            <p className="orderProviderEmpty">Noch keine Auftraege fuer diese Kartenfirma.</p>
          )}
          </div>
        ) : null}

        {providerDashboardTab === "members" ? (
          <div className="companyMembersPanel">
            <div className="orderProviderPricingHeader">
              <span>
                <b>Mitglieder</b>
                <small>Manager duerfen Profil und Preise bearbeiten. Mitglieder koennen Auftraege bearbeiten.</small>
              </span>
              <button type="button" onClick={() => this.loadCompanyMembers()} disabled={loading}>
                Aktualisieren
              </button>
            </div>

            <form className="companyMemberForm" onSubmit={this.addCompanyMember}>
              <label>
                Ingame-Name
                <input
                  type="text"
                  value={memberPlayerName}
                  onChange={(event) => this.updateField("memberPlayerName", event.target.value)}
                  placeholder="Registrierter Spielername"
                  required
                />
              </label>
              <label>
                Rolle
                <select value={memberRole} onChange={(event) => this.updateField("memberRole", event.target.value)}>
                  <option value="member">Mitglied</option>
                  <option value="editor">Manager</option>
                </select>
              </label>
              <button type="submit" disabled={loading}>
                Mitglied speichern
              </button>
            </form>

            <div className="companyMemberList">
              {companyJoinRequests.length ? (
                <div className="companyJoinRequestList">
                  <b>Mitarbeiter-Anfragen</b>
                  {companyJoinRequests.map((joinRequest) => (
                    <div className={`companyJoinRequestItem ${joinRequest.status}`} key={joinRequest.id}>
                      <span>
                        <strong>{joinRequest.playerName}</strong>
                        <small>{this.formatJoinRequestStatus(joinRequest.status)}</small>
                      </span>
                      <p>{joinRequest.message || "Keine Nachricht."}</p>
                      {joinRequest.status === "pending" ? (
                        <div className="actions">
                          <button
                            type="button"
                            onClick={() => this.resolveCompanyJoinRequest(joinRequest, "accepted")}
                            disabled={loading}
                          >
                            Annehmen
                          </button>
                          <button
                            type="button"
                            onClick={() => this.resolveCompanyJoinRequest(joinRequest, "declined")}
                            disabled={loading}
                          >
                            Ablehnen
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              {companyMembers.length ? (
                companyMembers.map((member) => (
                  <div className={`companyMemberItem ${member.active ? "" : "inactive"}`} key={member.id}>
                    <span>
                      <b>{member.playerName}</b>
                      <small>{member.active ? this.formatMemberRole(member.role) : "Deaktiviert"}</small>
                    </span>
                    {member.role === "owner" ? (
                      <strong>Firmenchef</strong>
                    ) : (
                      <div className="actions">
                        <select
                          value={member.role === "editor" ? "editor" : "member"}
                          onChange={(event) => this.updateCompanyMember(member, { role: event.target.value })}
                          disabled={loading || !member.active}
                        >
                          <option value="member">Mitglied</option>
                          <option value="editor">Manager</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => this.updateCompanyMember(member, { active: !member.active })}
                          disabled={loading}
                        >
                          {member.active ? "Entfernen" : "Reaktivieren"}
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="orderProviderEmpty">Noch keine Mitglieder geladen.</p>
              )}
            </div>
          </div>
        ) : null}

        {providerDashboardTab === "discord" ? (
          <form className="companyDiscordPanel" onSubmit={this.saveCompanyDiscordSettings}>
            <span>
              <b>Discord-Integration</b>
              <small>Waehle, wo Shiori neue Kartenauftraege fuer diese Firma anlegen soll.</small>
            </span>
            <div className="companyDiscordInviteBox">
              <span>
                <b>Shiori auf deinen Discord einladen</b>
                <small>
                  Der Bot braucht die Scopes bot und applications.commands. Die notwendigen Rechte sind bereits im
                  Einladungslink enthalten.
                </small>
              </span>
              {companyDiscordInviteUrl ? (
                <a href={companyDiscordInviteUrl} target="_blank" rel="noopener noreferrer">
                  Bot einladen
                </a>
              ) : (
                <small>Invite-Link nicht verfuegbar. Auf dem Server fehlt DISCORD_CLIENT_ID.</small>
              )}
              {companyDiscordRequiredPermissions.length ? (
                <small>Rechte: {companyDiscordRequiredPermissions.join(", ")}</small>
              ) : null}
            </div>
            <label className="orderProviderToggle">
              <input
                type="checkbox"
                checked={Boolean(companyDiscordSettings?.notificationsEnabled)}
                onChange={(event) => this.updateDiscordSettingField("notificationsEnabled", event.target.checked)}
              />
              <span>Automatische Discord-Tickets aktivieren</span>
            </label>
            <label>
              Discord-Server
              <select
                value={companyDiscordSettings?.guildId || ""}
                onChange={(event) => {
                  this.setState((state) => ({
                    companyDiscordSettings: {
                      ...(state.companyDiscordSettings || {}),
                      guildId: event.target.value,
                      targetChannelId: "",
                    },
                  }));
                }}
              >
                <option value="">Server waehlen</option>
                {companyDiscordGuilds.map((guild) => (
                  <option key={guild.id} value={guild.id}>
                    {guild.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Kategorie oder Forum
              <select
                value={companyDiscordSettings?.targetChannelId || ""}
                onChange={(event) => this.updateDiscordSettingField("targetChannelId", event.target.value)}
                disabled={!selectedDiscordGuild}
              >
                <option value="">Ziel waehlen</option>
                {discordTargets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.type === "forum" ? "Forum" : "Kategorie"} - {target.name}
                  </option>
                ))}
              </select>
            </label>
            {companyDiscordSettings?.targetChannelName ? (
              <small>
                Gespeichert: {companyDiscordSettings.guildName} / {companyDiscordSettings.targetChannelName}
              </small>
            ) : null}
            {!companyDiscordGuilds.length ? (
              <p>Shiori ist aktuell auf keinem Discord-Server sichtbar oder der Bot ist offline.</p>
            ) : null}
            <div className="actions">
              <button type="button" onClick={() => this.loadCompanyDiscordSettings()} disabled={loading}>
                Ziele aktualisieren
              </button>
              <button type="submit" disabled={loading || !companyDiscordSettings}>
                Discord-Ziel speichern
              </button>
            </div>
          </form>
        ) : null}
      </div>
    );
  }

  renderAuthForm() {
    const { captcha, loading, mode, password, playerName, turnstileToken, twoFactorCode } = this.state;

    return (
      <form className="orderAccountForm" onSubmit={this.submitAuth}>
        <div className="orderAccountModeSwitch" role="group" aria-label="Account Modus">
          <button type="button" className={mode === "login" ? "active" : ""} onClick={() => this.setMode("login")}>
            Login
          </button>
          <button type="button" className={mode === "register" ? "active" : ""} onClick={() => this.setMode("register")}>
            Registrierung
          </button>
        </div>

        <label>
          Ingame-Name
          <input
            type="text"
            value={playerName}
            onChange={(event) => this.updateField("playerName", event.target.value)}
            autoComplete="username"
            placeholder="z. B. Flammengott"
            required
          />
        </label>

        <label>
          Passwort
          <input
            type="password"
            value={password}
            onChange={(event) => this.updateField("password", event.target.value)}
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            required
          />
        </label>

        {mode === "login" ? (
          <label>
            2FA-Code
            <input
              type="text"
              inputMode="numeric"
              value={twoFactorCode}
              onChange={(event) => this.updateField("twoFactorCode", event.target.value)}
              autoComplete="one-time-code"
              placeholder="Nur wenn aktiviert"
              maxLength={6}
            />
          </label>
        ) : null}

        {mode === "register" ? (
          <div className="turnstileField">
            <span>Cloudflare-Sicherheitspruefung</span>
            {captcha?.siteKey ? (
              <div className="turnstileWidget" ref={this.turnstileContainerRef}></div>
            ) : (
              <small>Cloudflare Turnstile ist noch nicht konfiguriert.</small>
            )}
            {!turnstileToken ? <small>Bitte warte, bis die Sicherheitspruefung abgeschlossen ist.</small> : null}
          </div>
        ) : null}

        <button type="submit" disabled={loading || (mode === "register" && !turnstileToken)}>
          {mode === "register" ? "Account erstellen" : "Anmelden"}
        </button>
      </form>
    );
  }

  renderCompanyForm() {
    const {
      activeOrderTab,
      companies,
      companyDescription,
      companyName,
      latestCompany,
      loading,
      unreadDirectMessageCount,
      user,
    } = this.state;

    return (
      <div className="orderAccountCompanyArea">
        <div className="orderAccountUserRow">
          <span>
            Angemeldet als <b>{user.playerName}</b>
            <small>{user.twoFactorEnabled ? "2FA aktiv" : "2FA nicht aktiv"}</small>
          </span>
          <button type="button" onClick={this.logout} disabled={loading}>
            Logout
          </button>
        </div>


        <div className="orderAccountTabs" role="tablist" aria-label="Auftragssystem Bereiche">
          <button
            type="button"
            className={activeOrderTab === "create" ? "active" : ""}
            onClick={() => this.setActiveOrderTab("create")}
          >
            Auftrag erstellen
          </button>
          <button
            type="button"
            className={activeOrderTab === "profile" ? "active" : ""}
            onClick={() => this.setActiveOrderTab("profile")}
          >
            Mein Profil
          </button>
          <button
            type="button"
            className={activeOrderTab === "orders" ? "active" : ""}
            onClick={() => this.setActiveOrderTab("orders")}
          >
            Meine Auftraege
          </button>
          <button
            type="button"
            className={activeOrderTab === "companies" ? "active" : ""}
            onClick={() => this.setActiveOrderTab("companies")}
          >
            Firmen finden
          </button>
          <button
            type="button"
            className={activeOrderTab === "users" ? "active" : ""}
            onClick={() => this.setActiveOrderTab("users")}
          >
            Nutzer
          </button>
          <button
            type="button"
            className={activeOrderTab === "messages" ? "active" : ""}
            onClick={() => this.setActiveOrderTab("messages")}
          >
            Nachrichten{unreadDirectMessageCount ? ` (${unreadDirectMessageCount})` : ""}
          </button>
          <button
            type="button"
            className={activeOrderTab === "feedback" ? "active" : ""}
            onClick={() => this.setActiveOrderTab("feedback")}
          >
            Feedback
          </button>
          <button
            type="button"
            className={activeOrderTab === "provider" ? "active" : ""}
            onClick={() => this.setActiveOrderTab("provider")}
          >
            Firmen-Dashboard
          </button>
        </div>

        {activeOrderTab === "profile" ? this.renderUserProfilePanel() : null}
        {activeOrderTab === "create" ? this.renderOrderRequestForm() : null}
        {activeOrderTab === "orders" ? this.renderCustomerOrdersPanel() : null}
        {activeOrderTab === "companies" ? this.renderCompanyDiscoveryPanel() : null}
        {activeOrderTab === "users" ? this.renderUserDirectoryPanel() : null}
        {activeOrderTab === "messages" ? this.renderDirectMessagesPanel() : null}
        {activeOrderTab === "feedback" ? this.renderFeedbackPanel() : null}

        {activeOrderTab === "provider" ? (
          <React.Fragment>
            <form className="orderAccountForm orderCompanyForm" onSubmit={this.submitCompany}>
              <div className="orderCompanyHint">
                <b>Kartenfirma als Anbieter</b>
                <span>Nur anlegen, wenn du MapArt-Auftraege annehmen und spaeter im Anbieter-Dashboard verwalten willst.</span>
              </div>

              <label>
                Firmenname
                <input
                  type="text"
                  value={companyName}
                  onChange={(event) => this.updateField("companyName", event.target.value)}
                  placeholder="Name deiner Kartenfirma"
                  required
                />
              </label>

              <label>
                Beschreibung
                <textarea
                  value={companyDescription}
                  onChange={(event) => this.updateField("companyDescription", event.target.value)}
                  placeholder="Kurzbeschreibung fuer deine oeffentliche Auftragsseite"
                  rows={3}
                />
              </label>

              <button type="submit" disabled={loading}>
                Anbieter-Firma erstellen
              </button>
            </form>

            {latestCompany ? (
              <div className="orderAccountCompanyResult">
                <span>Oeffentliche Seite:</span>
                <code>/api/public/organizations/{latestCompany.slug}</code>
              </div>
            ) : null}

            {companies.length ? (
              <div className="orderAccountCompanyList">
                <span className="orderAccountCompanyListTitle">Meine Kartenfirmen</span>
                {companies.map((company) => (
                  <div className="orderAccountCompanyItem" key={company.slug}>
                    <span>
                      <b>{company.name}</b>
                      <small>
                        {company.ticketPrefix ? `${company.ticketPrefix} - ` : ""}
                        {company.memberRole || "owner"} - {company.acceptingOrders ? "nimmt Auftraege an" : "pausiert"}
                      </small>
                    </span>
                    <code>/{company.slug}</code>
                  </div>
                ))}
                <small>Firmen-Dashboard fuer Profil, Auftragstypen und Auftragsuebersicht.</small>
              </div>
            ) : null}

            {this.renderProviderDashboard()}
          </React.Fragment>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      achievementToast,
      companies,
      easterEggEffect,
      error,
      expanded,
      message,
      opsNetFlashActive,
      previewImageModal,
      profilePreviewOpen,
      activeTheme,
      lockedTheme,
      user,
    } = this.state;
    const accountStatus = user
      ? `${user.playerName}${companies.length ? ` - ${companies.length} Kartenfirma(en)` : ""}`
      : "Nicht angemeldet";
    const toastAchievement = achievementToast ? ACHIEVEMENT_BY_ID.get(achievementToast) : null;
    const normalizedActiveTheme = normalizeThemeKey(activeTheme);
    const normalizedLockedTheme = normalizeThemeKey(lockedTheme);
    const hasActiveTheme = normalizedActiveTheme !== "normal";
    const hasLockedTheme = normalizedLockedTheme !== "normal";

    return (
      <section className={`orderAccountPanel ${expanded ? "expanded" : "collapsed"}`}>
        <div className="orderAccountIntro">
          <div>
            <h2>Auftragssystem</h2>
            <p>
              Login, Auftraege und Anbieter-Dashboard. Einklappen, wenn du gerade nur den MapArt-Generator nutzen willst.
            </p>
          </div>
          <div className="orderAccountStatusRow">
            <span className="orderAccountStatus">{accountStatus}</span>
            <span className={`themeStatusBadge ${hasActiveTheme ? "active" : ""} ${hasLockedTheme ? "locked" : ""}`}>
              <b>{THEME_LABELS[normalizedActiveTheme]}</b>
              <small>{hasLockedTheme ? `Standard: ${THEME_LABELS[normalizedLockedTheme]}` : "Nicht gespeichert"}</small>
              <i aria-hidden="true">{hasLockedTheme ? "Lock" : "Offen"}</i>
            </span>
          </div>
          <button
            type="button"
            className="orderAccountToggleButton"
            onClick={this.toggleExpanded}
            aria-expanded={expanded}
          >
            {expanded ? "Einklappen" : "Aufklappen"}
          </button>
        </div>

        {expanded ? (
          <React.Fragment>
            {user ? this.renderCompanyForm() : this.renderAuthForm()}

            {message ? <p className="orderAccountMessage">{message}</p> : null}
            {error ? <p className="orderAccountError">{error}</p> : null}
          </React.Fragment>
        ) : null}

        {toastAchievement ? (
          <div className="achievementToast floating" role="status">
            <strong>Erfolg entdeckt</strong>
            <span>{toastAchievement.title}</span>
          </div>
        ) : null}

        {previewImageModal ? (
          <div
            className="orderImageModalBackdrop"
            role="presentation"
            onClick={() => this.setState({ previewImageModal: null })}
          >
            <div className="orderImageModal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
              <div>
                <b>{previewImageModal.title}</b>
                <button type="button" onClick={() => this.setState({ previewImageModal: null })}>
                  Schliessen
                </button>
              </div>
              <img src={previewImageModal.src} alt={`Grosse Vorschau ${previewImageModal.title}`} />
            </div>
          </div>
        ) : null}

        {profilePreviewOpen ? this.renderProfilePreviewModal() : null}
        {easterEggEffect ? (
          <div className={`easterEggEffect ${easterEggEffect}`} aria-hidden="true">
            <span></span>
            {easterEggEffect === "shiori" ? (
              <div className="shioriCore">
                <img src={shioriAvatar} alt="" />
                <i></i>
                <b></b>
              </div>
            ) : null}
            <strong>
              {easterEggEffect === "shiori"
                ? "Shiori"
                : easterEggEffect === "flame"
                  ? "Flammengott"
                  : easterEggEffect === "mapart"
                    ? "MapArt"
                    : easterEggEffect === "cipher"
                      ? "CB-100"
                      : easterEggEffect === "sync"
                        ? "Sync"
                        : "PHCO"}
            </strong>
          </div>
        ) : null}
        {opsNetFlashActive ? (
          <div className="opsNetFlash" aria-hidden="true">
            <span>OPSUCHT.NET</span>
            <strong>ONLINE</strong>
          </div>
        ) : null}
        <button
          type="button"
          className="hiddenFigure flammy"
          onClick={() => this.clickHiddenFigure("flammy")}
          aria-label="Versteckte Figur"
        >
          <span></span>
        </button>
        <button
          type="button"
          className="hiddenFigure phoenix"
          onClick={() => this.clickHiddenFigure("phoenix")}
          aria-label="Versteckte Figur"
        >
          <span></span>
        </button>
      </section>
    );
  }
}

export default OrderAccountPanel;
